import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { FiMaximize2, FiMinimize2, FiLock } from "react-icons/fi";
import { FaGithub } from "react-icons/fa";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints, urlAgentsDashboard, urlAdminRefreshDashboard } from "../config/api";
import MultiSelectDropdown from "./MultiSelectDropdown";
import DashboardThumbnailModal from "./DashboardThumbnailModal";
import { buildOrgMultiSelectOptions, parseOrgIdsFromMultiSelectValues } from "../utils/orgMultiSelectOptions";
import {
  buildUserMultiSelectOptions,
  formatUserPickerLabel,
  parseUserIdsFromMultiSelectValues,
} from "../utils/userMultiSelectOptions";
import { fetchAllOrgUsers, flattenOrgUsersForPicker } from "../utils/fetchOrgUsers";
import {
  buildDashboardsAdminParams,
  dashboardsAdminParamsSerializer,
  DASHBOARDS_ADMIN_DEFAULT_LIMIT,
  DASHBOARDS_ADMIN_MAX_LIMIT,
} from "../utils/buildDashboardsAdminParams";
import { refreshAgentCache } from "../utils/refreshAgentCache";
import { fetchSectorIndustryList } from "../utils/sectorIndustryList";
import {
  buildTickerOptionsFromStockSearch,
  mergeTickerAutofillSectorIndustry,
} from "../utils/stockSearchMetadata";
import {
  MAX_SECTION_DEPTH,
  buildSectionLadderLines,
  createSectionNode,
  formatSectionBadge,
  buildDashboardSectionsRequestBody,
  formatDashboardSectionsPayloadPreview,
  sectionNodesFromApi,
} from "../utils/dashboardSectionsTree";
import {
  formatKeywordsForInput,
  keywordsFromDashboardResponse,
  normalizeDashboardKeywords,
  parseKeywordsInput,
} from "../utils/dashboardKeywords";
import {
  isCorporateAssociatedDashboard,
  isCorporateUserType,
  orgIdHasCorporateUsers,
  resolveIsCorporateAssigned,
} from "../utils/dashboardAudience";
import {
  SHOW_IN_MY_DASHBOARDS_DEFAULT,
  coerceShowInMyDashboards,
  readShowInMyDashboards,
  showInMyDashboardsPayload,
} from "../utils/dashboardMyDashboard";
import {
  filterDashboardsVisibleToUser,
  formatDashboardAccessReason,
  getDashboardOrgIds,
  getDashboardUserIds,
  resolveDashboardAccessForUser,
  summarizeDashboardAccessForUser,
} from "../utils/dashboardUserVisibility";
import {
  computeNextRefreshAt,
  formatRefreshFrequency,
  formatStalenessTime,
  fromDateTimeInputValue,
  isDashboardOverdue,
  manualRefreshPayload,
  nextRefreshAt,
  readRefreshTimestamps,
  readStalenessFields,
  refreshFrequencyOptions,
  refreshFrequencyPayload,
  stalenessTimestampsPayload,
  toDateTimeInputValue,
} from "../utils/dashboardStaleness";

/** Width the 14-column list table needs before its cells start getting crushed. */
const LIST_TABLE_MIN_WIDTH = 1600;
/** Table zoom steps — 100 % down to 60 %, so a wide table can be fitted in place. */
const TABLE_ZOOM_MIN = 0.6, TABLE_ZOOM_MAX = 1, TABLE_ZOOM_STEP = 0.1;
const clampTableZoom = (z) =>
  Math.min(TABLE_ZOOM_MAX, Math.max(TABLE_ZOOM_MIN, Math.round(z * 100) / 100));

const shimmerCellStyle = {
  height: "20px",
  borderRadius: "4px",
  background: "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)",
  backgroundSize: "200% 100%",
  animation: "dashboardsShimmer 1.5s ease-in-out infinite",
};

const ShimmerRow = ({ colCount = 8 }) => (
  <tr>
    {Array.from({ length: colCount }, (_, i) => (
      <td key={i} style={{ padding: "12px 14px", border: "1px solid #e5e7eb" }}>
        <div style={shimmerCellStyle} />
      </td>
    ))}
  </tr>
);

const fetchDashboardsAdminPage = async (queryOverrides = {}) => {
  const response = await axios.get(endpoints.agentsDashboardsAdmin, {
    params: buildDashboardsAdminParams(queryOverrides),
    paramsSerializer: dashboardsAdminParamsSerializer,
    headers: {
      ...getAuthHeaders(),
      Accept: "*/*",
      "Content-Type": "application/json",
    },
  });
  const body = response.data || {};
  const data = Array.isArray(body.data) ? body.data.map(normalizeDashboardFromApi) : [];
  const total = typeof body.total === "number" ? body.total : data.length;
  return { data, total };
};

const fetchAllDashboardsAdmin = async (queryOverrides = {}) => {
  const collected = [];
  let currentPage = 1;
  let totalFromApi = null;

  while (true) {
    const { data: chunk, total } = await fetchDashboardsAdminPage({
      ...queryOverrides,
      page: currentPage,
      limit: DASHBOARDS_ADMIN_MAX_LIMIT,
    });
    collected.push(...chunk);
    totalFromApi = total;
    if (chunk.length === 0) break;
    if (totalFromApi != null && collected.length >= totalFromApi) break;
    if (chunk.length < DASHBOARDS_ADMIN_MAX_LIMIT) break;
    currentPage += 1;
  }

  return collected;
};

const normalizeOrgId = (value) => {
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const getDashboardOrgVisibilityLabel = (dashboard, availableOrgs) => {
  const raw = dashboard?.organizations;
  if (!Array.isArray(raw) || raw.length === 0) return "Shown to all users";
  const list = Array.isArray(availableOrgs) ? availableOrgs : [];
  return raw
    .map((org) => {
      let name = "";
      if (org != null && typeof org === "object") {
        name = org.name || org.org_name || "";
      }
      const id =
        org != null && typeof org === "object"
          ? normalizeOrgId(org.id ?? org.organization_id)
          : normalizeOrgId(org);
      if (!name && id != null) {
        const fromApi = list.find((o) => Number(o.id) === id);
        name = fromApi?.name ?? `Org ${id}`;
      }
      if (!name) return "";
      const corporate =
        org != null &&
        typeof org === "object" &&
        (org.isCorporate === true || org.hasCorporateUsers === true);
      return corporate ? `${name} (Corporate)` : name;
    })
    .filter(Boolean)
    .join(", ");
};

const getDashboardUserVisibilityLabel = (dashboard) => {
  const userIds = getDashboardUserIds(dashboard);
  const orgIds = getDashboardOrgIds(dashboard);
  const isPublic =
    dashboard?.isPublic === true ||
    (orgIds.length === 0 && userIds.length === 0);

  if (userIds.length > 0) {
    const embedded = Array.isArray(dashboard?.users) ? dashboard.users : [];
    const names = userIds.map((id) => {
      const match = embedded.find((u) => Number(u.user_id) === Number(id));
      if (match?.name) {
        return isCorporateUserType(match.user_type) ? `${match.name} (Corporate)` : match.name;
      }
      if (match?.email) {
        return isCorporateUserType(match.user_type)
          ? `${match.email} (Corporate)`
          : match.email;
      }
      return `User ${id}`;
    });
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} (+${names.length - 2} more)`;
  }

  if (isPublic) return "Shown to all users";
  if (orgIds.length > 0) {
    return `All users in ${orgIds.length} org${orgIds.length === 1 ? "" : "s"}`;
  }
  return "No user restriction";
};

const areSameIds = (a, b) => {
  const aa = [...(a || [])].map(Number).filter((x) => !Number.isNaN(x)).sort((x, y) => x - y);
  const bb = [...(b || [])].map(Number).filter((x) => !Number.isNaN(x)).sort((x, y) => x - y);
  if (aa.length !== bb.length) return false;
  return aa.every((v, idx) => v === bb[idx]);
};

const coerceApiBoolean = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const normalizeDashboardOrganizationsFromApi = (organizations) => {
  if (!Array.isArray(organizations)) return [];
  return organizations.map((org) => {
    if (org == null || typeof org !== "object") return org;
    return {
      ...org,
      isCorporate: coerceApiBoolean(org.isCorporate),
      hasCorporateUsers: coerceApiBoolean(org.hasCorporateUsers),
    };
  });
};

const normalizeDashboardUsersFromApi = (users) => {
  if (!Array.isArray(users)) return [];
  return users.map((user) => {
    if (user == null || typeof user !== "object") return user;
    return {
      ...user,
      user_type: user.user_type ?? user.userType ?? null,
    };
  });
};

const normalizeDashboardFromApi = (dashboard) => ({
  ...dashboard,
  sections: dashboard?.sections ?? null,
  keywords: normalizeDashboardKeywords(dashboard?.keywords),
  audience: dashboard?.audience ?? null,
  // The admin list sends staleness fields in camelCase; the form and the
  // staleness table read snake_case.
  ...readStalenessFields(dashboard),
  isCorporateAssigned: coerceApiBoolean(dashboard?.isCorporateAssigned),
  showInMyDashboards: readShowInMyDashboards(dashboard),
  corporateAssigneeCount:
    typeof dashboard?.corporateAssigneeCount === "number"
      ? dashboard.corporateAssigneeCount
      : 0,
  isPublic: coerceApiBoolean(dashboard?.isPublic),
  organizations: normalizeDashboardOrganizationsFromApi(dashboard?.organizations),
  users: normalizeDashboardUsersFromApi(dashboard?.users),
  userIds: Array.isArray(dashboard?.userIds)
    ? dashboard.userIds.map(Number).filter((n) => !Number.isNaN(n))
    : undefined,
});

const corporateAudiencePillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "2px 8px",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.01em",
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  border: "1px solid #99f6e4",
  whiteSpace: "nowrap",
};

/** Palette per access reason for the "visible to user" row pill. */
const ACCESS_REASON_COLORS = {
  user: { bg: "#e0e7ff", fg: "#3730a3", border: "#c7d2fe" },
  org: { bg: "#dbeafe", fg: "#1d4ed8", border: "#bfdbfe" },
  public: { bg: "#f3f4f6", fg: "#4b5563", border: "#e5e7eb" },
};

const accessReasonPillStyle = (reason) => {
  const c = ACCESS_REASON_COLORS[reason] || ACCESS_REASON_COLORS.public;
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 600,
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${c.border}`,
    whiteSpace: "nowrap",
  };
};

const ACCESS_REASON_HINTS = {
  user: "This user is on the dashboard's user allowlist.",
  org: "This user's organisation is granted access to the dashboard.",
  public: "No org or user restriction — every user sees this dashboard.",
};

const userFilterBannerStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "12px",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#eef2ff",
  fontSize: "13px",
  color: "#3730a3",
};

const corporateMiniChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  marginLeft: "6px",
  padding: "1px 6px",
  borderRadius: "999px",
  fontSize: "10px",
  fontWeight: 600,
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  border: "1px solid #99f6e4",
  verticalAlign: "middle",
};

const assigneeChipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "6px",
  marginTop: "6px",
};

const assigneeChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 8px",
  borderRadius: "6px",
  fontSize: "12px",
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #e5e7eb",
};

/** Merge UI sector + industry picks into one `sectors` array for the API. */
const mergeDashboardSectorPayload = (sectors, industries) => {
  const combined = [
    ...(Array.isArray(sectors) ? sectors : []),
    ...(Array.isArray(industries) ? industries : []),
  ];
  return [...new Set(combined.map(String).filter(Boolean))];
};

/** Split combined API `sectors` back into sector vs industry form fields. */
const splitDashboardSectorsFromApi = (dashboard, sectorNames, industryNames) => {
  const sectorSet = new Set(sectorNames);
  const industrySet = new Set(industryNames);
  const combined = [
    ...(Array.isArray(dashboard?.sectors) ? dashboard.sectors : []),
    ...(Array.isArray(dashboard?.industries) ? dashboard.industries : []),
  ];
  const unique = [...new Set(combined.map(String).filter(Boolean))];
  const sectors = [];
  const industries = [];
  for (const name of unique) {
    if (sectorSet.has(name)) sectors.push(name);
    else if (industrySet.has(name)) industries.push(name);
    else sectors.push(name);
  }
  return { sectors, industries };
};

const updateNodeInTree = (nodes, nodeId, updater) =>
  (nodes || []).map((node) => {
    if (node.id === nodeId) return updater(node);
    if (Array.isArray(node.children) && node.children.length > 0) {
      return { ...node, children: updateNodeInTree(node.children, nodeId, updater) };
    }
    return node;
  });

const removeNodeFromTree = (nodes, nodeId) =>
  (nodes || [])
    .filter((node) => node.id !== nodeId)
    .map((node) => ({
      ...node,
      children: removeNodeFromTree(node.children, nodeId),
    }));

const addChildToNode = (nodes, parentId, child) =>
  (nodes || []).map((node) => {
    if (node.id === parentId) {
      return { ...node, children: [...(node.children || []), child] };
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      return { ...node, children: addChildToNode(node.children, parentId, child) };
    }
    return node;
  });

const expandIconButtonStyle = {
  position: "absolute",
  top: 0,
  right: 0,
  padding: "2px",
  margin: 0,
  border: "none",
  borderRadius: "4px",
  background: "rgba(255, 255, 255, 0.92)",
  color: "#4338ca",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 0,
  zIndex: 1,
};

/**
 * Truncated list/table cell with expand/collapse icon (top-right).
 * Pass either `text` (string) or `lines` (string[]). Empty shows em dash.
 */
const ExpandableCell = ({
  text,
  lines,
  emptyLabel = "—",
  emptyMuted = true,
  collapsedMaxHeight = 40,
  mono = false,
  title,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const lineList = React.useMemo(() => {
    if (Array.isArray(lines)) {
      return lines.map((l) => String(l ?? "")).filter((l) => l.length > 0);
    }
    return null;
  }, [lines]);

  const displayText = text == null ? "" : String(text);
  const hasContent = lineList ? lineList.length > 0 : displayText.trim().length > 0;

  if (!hasContent) {
    return (
      <span style={{ color: emptyMuted ? "#9ca3af" : "#374151", fontSize: "12px" }}>
        {emptyLabel}
      </span>
    );
  }

  const canExpand =
    (lineList && (lineList.length > 1 || lineList.join("").length > 28)) ||
    (!lineList && displayText.length > 36);

  const contentStyle = {
    fontSize: mono ? "11px" : "13px",
    lineHeight: 1.45,
    color: "#374151",
    paddingRight: canExpand ? "18px" : 0,
    fontFamily: mono
      ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
      : "inherit",
    ...(expanded
      ? {
          whiteSpace: lineList ? "pre" : "pre-wrap",
          wordBreak: "break-word",
          overflow: "visible",
          maxHeight: "none",
        }
      : {
          maxHeight: collapsedMaxHeight,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: lineList ? "pre" : "nowrap",
        }),
  };

  const fullTitle = title || (lineList ? lineList.join("\n") : displayText);

  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      {canExpand && (
        <button
          type="button"
          style={expandIconButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse cell" : "Expand cell"}
          title={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? (
            <FiMinimize2 size={12} aria-hidden="true" />
          ) : (
            <FiMaximize2 size={12} aria-hidden="true" />
          )}
        </button>
      )}
      <div style={contentStyle} title={expanded ? undefined : fullTitle}>
        {lineList
          ? lineList.map((line, i) => (
              <div key={`${line}-${i}`} style={{ minHeight: "16px" }}>
                {line}
              </div>
            ))
          : displayText}
      </div>
    </div>
  );
};

const DashboardSectionsTableCell = ({ sections }) => {
  const ladderLines = React.useMemo(() => {
    const nodes = sectionNodesFromApi(sections);
    return buildSectionLadderLines(nodes, false);
  }, [sections]);

  return (
    <ExpandableCell
      lines={ladderLines}
      emptyLabel="—"
      collapsedMaxHeight={56}
      mono
    />
  );
};

const stalenessFieldLabelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "6px",
};

const stalenessFieldHintStyle = {
  fontSize: "11px",
  color: "#6b7280",
  marginTop: "4px",
  lineHeight: 1.4,
};

const stalenessInputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  fontSize: "13px",
  backgroundColor: "#fff",
  color: "#111827",
  minHeight: "38px",
  boxSizing: "border-box",
};

/**
 * Editable timestamp field inside the Data Staleness Management panel. `warn`
 * flags a required field that is still empty — a prompt only, never a blocker.
 */
const StalenessDateField = ({ id, label, required, warn, value, onChange, hint, children }) => (
  <div style={{ flex: "1 1 220px", minWidth: "200px" }}>
    <label htmlFor={id} style={stalenessFieldLabelStyle}>
      {label}
      {required ? <span style={{ color: "#b45309" }}> *</span> : null}
    </label>
    <input
      id={id}
      type="datetime-local"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={warn ? { ...stalenessInputStyle, borderColor: "#fcd34d" } : stalenessInputStyle}
    />
    {children}
    {hint ? (
      <div style={warn ? { ...stalenessFieldHintStyle, color: "#b45309" } : stalenessFieldHintStyle}>
        {hint}
      </div>
    ) : null}
  </div>
);

/** Type-to-confirm delete modal, matching the one in Modify Library Agents. */
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
  padding: "16px",
};

const modalContentStyle = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "24px",
  maxWidth: "440px",
  width: "100%",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
};

const modalTitleStyle = {
  fontSize: "18px",
  fontWeight: 700,
  color: "#1f2937",
  marginBottom: "8px",
};

const modalBodyStyle = {
  fontSize: "14px",
  color: "#4b5563",
  marginBottom: "16px",
};

const modalDangerInputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  border: "1px solid #ef4444",
  borderRadius: "6px",
  boxSizing: "border-box",
  marginBottom: "16px",
};

const modalButtonsStyle = {
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
};

const modalCancelButtonStyle = {
  padding: "9px 16px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  backgroundColor: "#fff",
  color: "#374151",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const modalDeleteButtonStyle = {
  padding: "9px 16px",
  borderRadius: "6px",
  border: "none",
  backgroundColor: "#dc2626",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const modalDeleteButtonDisabledStyle = {
  ...modalDeleteButtonStyle,
  backgroundColor: "#fca5a5",
  cursor: "not-allowed",
};

const DASHBOARD_TABS = [
  { id: "list", label: "List Dashboards" },
  { id: "create", label: "Create Dashboard" },
  { id: "staleness", label: "Data Staleness" },
  { id: "orgVisibility", label: "Org Visibility" },
  { id: "userVisibility", label: "User Visibility" },
];

const Dashboards = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("list");
  const isListTab = activeTab === "list";
  const isCreateTab = activeTab === "create";
  const isOrgVisibilityView = activeTab === "orgVisibility";
  const isUserVisibilityView = activeTab === "userVisibility";
  const isVisibilityView = isOrgVisibilityView || isUserVisibilityView;
  const isStalenessTab = activeTab === "staleness";
  const tableColCount = isListTab ? 14 : isStalenessTab ? 7 : isVisibilityView ? 5 : 0;
  const [tableZoom, setTableZoom] = useState(1);
  const tableWrapperRef = useRef(null);
  const [dashboards, setDashboards] = useState([]);
  const [totalDashboards, setTotalDashboards] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [allDashboardsForOrgEdit, setAllDashboardsForOrgEdit] = useState([]);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [availableTickers, setAvailableTickers] = useState([]);
  const [tickerMetadataBySymbol, setTickerMetadataBySymbol] = useState({});
  const [sectorIndustrySectors, setSectorIndustrySectors] = useState([]);
  const [sectorIndustryFlattened, setSectorIndustryFlattened] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [debouncedCategory, setDebouncedCategory] = useState("");
  const [filterTickers, setFilterTickers] = useState([]);
  const [filterSectors, setFilterSectors] = useState([]);
  const [filterIndustries, setFilterIndustries] = useState([]);
  const [filterFavourite, setFilterFavourite] = useState(false);
  const [filterCorporateView, setFilterCorporateView] = useState(false);
  /** user_index (as string) of the "visible to user" filter; "" = off. */
  const [filterUserId, setFilterUserId] = useState("");
  const [userScopeDashboards, setUserScopeDashboards] = useState([]);
  const [loadingUserScope, setLoadingUserScope] = useState(false);
  const [orgVisibilityScope, setOrgVisibilityScope] = useState("all"); // all | all_users | customers
  const [orgScopeAllDashboards, setOrgScopeAllDashboards] = useState([]);
  const [loadingOrgScope, setLoadingOrgScope] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [orgVisibilityEditable, setOrgVisibilityEditable] = useState(false);
  const [orgVisibilityValues, setOrgVisibilityValues] = useState({});
  const [savingOrgVisibility, setSavingOrgVisibility] = useState(false);
  const [userVisibilityEditable, setUserVisibilityEditable] = useState(false);
  const [userVisibilityValues, setUserVisibilityValues] = useState({});
  const [savingUserVisibility, setSavingUserVisibility] = useState(false);
  const [allDashboardsForUserEdit, setAllDashboardsForUserEdit] = useState([]);
  const [orgUsersForPicker, setOrgUsersForPicker] = useState([]);

  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [formValues, setFormValues] = useState({
    id: null,
    title: "",
    category: "",
    link: "",
    githubLink: "",
    description: "",
    type: "iframe",
    widgetConfig: "",
    organizationIds: [],
    userIds: [],
    tickers: [],
    sectors: [],
    industries: [],
    sectionItems: [],
    keywords: [],
    isCorporateAssigned: false,
    showInMyDashboards: SHOW_IN_MY_DASHBOARDS_DEFAULT,
    organizationsMeta: [],
    usersMeta: [],
    refresh_frequency_hours: "",
    last_refreshed_at: null,
    next_refresh_at: null,
    nextDueManual: false,
  });
  const [keywordsInput, setKeywordsInput] = useState("");
  const [extractingKeywords, setExtractingKeywords] = useState(false);
  const [keywordsStatus, setKeywordsStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingError, setSavingError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'saveEdit' | 'delete', dashboard?: {...} }
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [stalenessData, setStalenessData] = useState([]);
  const [loadingStaleness, setLoadingStaleness] = useState(false);
  const [filterOverdueOnly, setFilterOverdueOnly] = useState(false);
  const [refreshingDashboards, setRefreshingDashboards] = useState({});

  useEffect(() => {
    if (!isOrgVisibilityView || orgVisibilityScope === "all" || orgVisibilityEditable) {
      setOrgScopeAllDashboards([]);
      setLoadingOrgScope(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingOrgScope(true);
      try {
        const all = await fetchAllDashboardsAdmin({
          search: debouncedSearch || undefined,
        });
        if (!cancelled) setOrgScopeAllDashboards(all);
      } catch (err) {
        console.error("Error loading dashboards for org visibility filter", err);
        if (!cancelled) {
          setOrgScopeAllDashboards([]);
          setError(getErrorMessage(err, "Failed to load dashboards for visibility filter"));
        }
      } finally {
        if (!cancelled) setLoadingOrgScope(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOrgVisibilityView, orgVisibilityScope, orgVisibilityEditable, debouncedSearch, refreshToken]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setListPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedCategory(filterCategory.trim());
      setListPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [filterCategory]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await axios.get(endpoints.dashboardsOrgs, { headers: { ...getAuthHeaders() } });
        setAvailableOrgs(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching orgs for dashboards", err);
      }
    };
    fetchOrgs();
  }, []);

  useEffect(() => {
    const loadOrgUsers = async () => {
      try {
        const groups = await fetchAllOrgUsers(endpoints.dashboardsOrgUsers, { limit: 100 });
        setOrgUsersForPicker(flattenOrgUsersForPicker(groups));
      } catch (err) {
        console.error("Error fetching org users for dashboards", err);
      }
    };
    loadOrgUsers();
  }, []);

  useEffect(() => {
    const fetchTickers = async () => {
      try {
        const response = await axios.post(
          endpoints.stockSearch,
          { query: "" },
          { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
        );
        const results = response.data?.data?.results;
        if (results && typeof results === "object") {
          const { options, metadataByTicker } = buildTickerOptionsFromStockSearch(results);
          setAvailableTickers(options);
          setTickerMetadataBySymbol(metadataByTicker);
        }
      } catch (err) {
        console.error("Error fetching tickers for dashboards", err);
      }
    };
    fetchTickers();
  }, []);

  useEffect(() => {
    const loadSectorIndustryList = async () => {
      try {
        const { sectors, flattened } = await fetchSectorIndustryList();
        setSectorIndustrySectors(Array.isArray(sectors) ? sectors : []);
        setSectorIndustryFlattened(Array.isArray(flattened) ? flattened : []);
      } catch (err) {
        console.error("Error fetching sector-industry list for dashboards", err);
      }
    };
    loadSectorIndustryList();
  }, []);

  const availableSectorOptions = useMemo(() => {
    return (sectorIndustrySectors || [])
      .map((s) => s?.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [sectorIndustrySectors]);

  const availableIndustryOptions = useMemo(() => {
    const seen = new Set();
    const names = [];
    for (const tuple of sectorIndustryFlattened || []) {
      const name = tuple?.industry;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
    return names
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [sectorIndustryFlattened]);

  useEffect(() => {
    const loadCategoryOptions = async () => {
      try {
        const all = await fetchAllDashboardsAdmin();
        const cats = [...new Set(all.map((d) => (d.category || "").trim()).filter(Boolean))];
        setCategoryOptions(cats.sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        console.error("Error loading dashboard categories", err);
      }
    };
    loadCategoryOptions();
  }, [refreshToken]);

  useEffect(() => {
    if (!isStalenessTab) return;
    let cancelled = false;
    const load = async () => {
      setLoadingStaleness(true);
      try {
        const all = await fetchAllDashboardsAdmin();
        if (!cancelled) setStalenessData(all);
      } catch (err) {
        console.error("Error fetching staleness data", err);
      } finally {
        if (!cancelled) setLoadingStaleness(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isStalenessTab, refreshToken]);

  useEffect(() => {
    const fetchDashboards = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, total } = await fetchDashboardsAdminPage({
          page: listPage,
          limit: DASHBOARDS_ADMIN_DEFAULT_LIMIT,
          search: debouncedSearch,
          category: debouncedCategory,
          tickers: filterTickers,
          sectors: filterSectors,
          industries: filterIndustries,
          favourite: filterFavourite ? true : undefined,
          isCorporateAssigned: filterCorporateView ? true : undefined,
        });
        setDashboards(data);
        setTotalDashboards(total);
      } catch (err) {
        console.error("Error fetching dashboards", err);
        setError(getErrorMessage(err, "Failed to load dashboards"));
        setDashboards([]);
        setTotalDashboards(0);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboards();
  }, [refreshToken, listPage, debouncedSearch, debouncedCategory, filterTickers, filterSectors, filterIndustries, filterFavourite, filterCorporateView]);

  /**
   * Directory entry for the selected "visible to user" filter. The picker is
   * flattened per org, so a user listed in several orgs has several rows — all of
   * their org ids count towards org-granted access.
   */
  const selectedFilterUser = useMemo(() => {
    if (!filterUserId) return null;
    const rows = orgUsersForPicker.filter(
      (u) => String(u.userIndex) === String(filterUserId)
    );
    if (rows.length === 0) return null;
    return {
      ...rows[0],
      organizationIds: rows.map((r) => r.organizationId).filter((id) => id != null),
      orgName: [...new Set(rows.map((r) => r.orgName).filter(Boolean))].join(", "),
    };
  }, [filterUserId, orgUsersForPicker]);

  /**
   * The admin list API filters by dashboard, not by viewer, so "visible to user"
   * is resolved client-side: pull every dashboard that matches the server-side
   * filters, then keep the ones this user can reach (direct grant, their org, or
   * public). Paging is done locally over that result.
   */
  useEffect(() => {
    if (!isListTab || !filterUserId) {
      setUserScopeDashboards([]);
      setLoadingUserScope(false);
      return undefined;
    }
    // Wait for the user directory before filtering — the org grant needs org id.
    if (!selectedFilterUser) {
      setUserScopeDashboards([]);
      setLoadingUserScope(true);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingUserScope(true);
      try {
        const all = await fetchAllDashboardsAdmin({
          search: debouncedSearch,
          category: debouncedCategory,
          tickers: filterTickers,
          sectors: filterSectors,
          industries: filterIndustries,
          favourite: filterFavourite ? true : undefined,
          isCorporateAssigned: filterCorporateView ? true : undefined,
        });
        if (!cancelled) setUserScopeDashboards(all);
      } catch (err) {
        console.error("Error loading dashboards for user visibility filter", err);
        if (!cancelled) {
          setUserScopeDashboards([]);
          setError(getErrorMessage(err, "Failed to load dashboards for the selected user"));
        }
      } finally {
        if (!cancelled) setLoadingUserScope(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isListTab, filterUserId, selectedFilterUser, refreshToken, debouncedSearch, debouncedCategory, filterTickers, filterSectors, filterIndustries, filterFavourite, filterCorporateView]);

  const userFilterActive = isListTab && Boolean(filterUserId);

  /** Dashboards the selected user can see, or null when the filter is off. */
  const userVisibleDashboards = useMemo(() => {
    if (!userFilterActive || !selectedFilterUser) return null;
    return filterDashboardsVisibleToUser(userScopeDashboards, selectedFilterUser);
  }, [userFilterActive, selectedFilterUser, userScopeDashboards]);

  /** Directory options for the user filter, grouped by org and searchable. */
  const userFilterOptions = useMemo(
    () => buildUserMultiSelectOptions(orgUsersForPicker, []),
    [orgUsersForPicker]
  );

  const userAccessSummary = useMemo(() => {
    if (!userFilterActive || !selectedFilterUser) return null;
    return summarizeDashboardAccessForUser(userScopeDashboards, selectedFilterUser);
  }, [userFilterActive, selectedFilterUser, userScopeDashboards]);

  const distinctCategories = categoryOptions;

  const closeButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginBottom: "20px",
    alignSelf: "flex-start",
  };

  const titleStyle = {
    color: "#3730a3",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "20px",
  };

  const searchInputStyle = {
    width: "100%",
    maxWidth: "400px",
    padding: "12px 14px 12px 40px",
    fontSize: "14px",
    border: "1px solid #3730a3",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#1f2937",
    outline: "none",
    boxSizing: "border-box",
  };

  const searchWrapperStyle = {
    position: "relative",
    marginBottom: "20px",
    width: "100%",
    maxWidth: "400px",
  };

  const searchIconStyle = {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
    fontSize: "18px",
  };

  /**
   * The list tab has 14 columns, so it needs more room than the panel gives it:
   * the table keeps a minimum width and the wrapper scrolls sideways instead of
   * squeezing every cell. Zooming out shrinks that minimum, so the whole table
   * can be brought back inside the panel without scrolling.
   */
  const editingVisibility =
    isVisibilityView && (orgVisibilityEditable || userVisibilityEditable);

  const tableWrapperStyle = {
    width: "100%",
    maxWidth: "100%",
    // popout dropdowns must not be clipped while editing visibility
    overflowX: editingVisibility ? "visible" : "auto",
    overflowY: editingVisibility ? "visible" : "auto",
    maxHeight: editingVisibility ? "none" : "70vh",
  };

  const tableStyle = {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: `${(14 * tableZoom).toFixed(1)}px`,
    minWidth: isListTab ? `${Math.round(LIST_TABLE_MIN_WIDTH * tableZoom)}px` : undefined,
  };

  const cellPadding = `${Math.round(10 * tableZoom)}px ${Math.round(8 * tableZoom)}px`;

  const tableZoomButtonStyle = {
    height: "28px",
    padding: 0,
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#374151",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: 1,
    cursor: "pointer",
  };

  /** Zooms out just enough for the whole table to fit the panel width. */
  const fitTableToWidth = () => {
    const box = tableWrapperRef.current;
    if (!box || !isListTab) {
      setTableZoom(1);
      return;
    }
    setTableZoom(clampTableZoom(box.clientWidth / LIST_TABLE_MIN_WIDTH));
  };

  const thStyle = {
    textAlign: "left",
    padding: cellPadding,
    backgroundColor: "#3730a3",
    color: "#fff",
    fontWeight: "600",
    border: "1px solid #3730a3",
  };

  const tdStyle = {
    padding: cellPadding,
    border: "1px solid #e5e7eb",
    color: "#1f2937",
    verticalAlign: "top",
  };

  const trEvenStyle = {
    backgroundColor: "#f9fafb",
  };

  const trOrgChangedStyle = {
    backgroundColor: "#dbeafe",
  };

  const orgToolbarButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
  };

  const tabBarStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "24px",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: "12px",
  };

  const tabButtonStyle = (active) => ({
    padding: "10px 16px",
    borderRadius: "8px",
    border: active ? "1px solid #3730a3" : "1px solid #d1d5db",
    backgroundColor: active ? "#3730a3" : "#ffffff",
    color: active ? "#ffffff" : "#374151",
    fontSize: "14px",
    fontWeight: active ? "600" : "500",
    cursor: "pointer",
    transition: "background-color 0.15s ease, border-color 0.15s ease",
  });

  const sectionPanelStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "12px",
    background: "linear-gradient(180deg, #f8faff 0%, #f3f4f6 100%)",
    boxShadow: "0 3px 12px rgba(17, 24, 39, 0.06)",
  };

  const sectionRowStyle = {
    border: "1px solid #dbe3f4",
    borderRadius: "10px",
    padding: "10px",
    backgroundColor: "#ffffff",
  };

  const addButtonStyle = {
    border: "1px solid #3730a3",
    backgroundColor: "#eef2ff",
    color: "#3730a3",
    borderRadius: "8px",
    cursor: "pointer",
    width: "34px",
    height: "34px",
    fontSize: "20px",
    fontWeight: "700",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const removeButtonStyle = {
    border: "1px solid #ef4444",
    color: "#b91c1c",
    backgroundColor: "#fff1f2",
    borderRadius: "8px",
    cursor: "pointer",
    width: "34px",
    height: "34px",
    fontSize: "18px",
    fontWeight: "700",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  /** Keeps next due in step with last refreshed + frequency until it is typed by hand. */
  const applyStalenessChange = (patch) => {
    setFormValues((prev) => {
      const next = { ...prev, ...patch };
      if (!next.nextDueManual) {
        next.next_refresh_at = computeNextRefreshAt(
          next.last_refreshed_at,
          next.refresh_frequency_hours
        );
      }
      return next;
    });
  };

  const handleLastRefreshedChange = (inputValue) => {
    applyStalenessChange({ last_refreshed_at: fromDateTimeInputValue(inputValue) });
  };

  /** A refresh done by hand becomes the last refresh; next due is re-derived from it. */
  const handleMarkRefreshedToday = () => {
    applyStalenessChange({ last_refreshed_at: new Date().toISOString(), nextDueManual: false });
  };

  const handleNextDueChange = (inputValue) => {
    const iso = fromDateTimeInputValue(inputValue);
    if (!iso) {
      applyStalenessChange({ nextDueManual: false });
      return;
    }
    setFormValues((prev) => ({ ...prev, next_refresh_at: iso, nextDueManual: true }));
  };

  // Flagged on the UI only — an empty last refresh never blocks saving.
  const missingLastRefreshed = !formValues.last_refreshed_at;

  const handleTickersChange = (nextTickers) => {
    setFormValues((prev) => {
      const tickers = Array.isArray(nextTickers) ? nextTickers : [];
      const { sectors, industries } = mergeTickerAutofillSectorIndustry(
        prev,
        prev.tickers || [],
        tickers,
        tickerMetadataBySymbol
      );
      return { ...prev, tickers, sectors, industries };
    });
  };

  const resetForm = () => {
    setFormMode("create");
    setFormValues({
      id: null,
      title: "",
      category: "",
      link: "",
      githubLink: "",
      description: "",
      type: "iframe",
      widgetConfig: "",
      organizationIds: [],
      userIds: [],
      tickers: [],
      sectors: [],
      industries: [],
      sectionItems: [],
      keywords: [],
      isCorporateAssigned: false,
      showInMyDashboards: SHOW_IN_MY_DASHBOARDS_DEFAULT,
      organizationsMeta: [],
      usersMeta: [],
      refresh_frequency_hours: "",
      last_refreshed_at: null,
      next_refresh_at: null,
    });
    setKeywordsInput("");
    setKeywordsStatus("");
    setSavingError(null);
  };

  const handleTabChange = (tabId) => {
    if (orgVisibilityEditable) handleCancelOrgVisibility();
    if (userVisibilityEditable) handleCancelUserVisibility();
    if (tabId === "create") {
      resetForm();
    }
    if (tabId !== "orgVisibility") {
      setOrgVisibilityScope("all");
      setOrgScopeAllDashboards([]);
    }
    setActiveTab(tabId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (dashboard) => {
    const sectorNames = (sectorIndustrySectors || []).map((s) => s?.name).filter(Boolean);
    const industryNames = [
      ...new Set((sectorIndustryFlattened || []).map((t) => t?.industry).filter(Boolean)),
    ];
    const { sectors, industries } = splitDashboardSectorsFromApi(
      dashboard,
      sectorNames,
      industryNames
    );
    setFormMode("edit");
    const keywords = normalizeDashboardKeywords(dashboard.keywords);
    const staleness = readStalenessFields(dashboard);
    setFormValues({
      id: dashboard.id,
      title: dashboard.title || "",
      category: dashboard.category || "",
      link: dashboard.link || "",
      githubLink: dashboard.githubLink || dashboard.github_link || "",
      description: dashboard.description || "",
      type: dashboard.type || "iframe",
      widgetConfig: dashboard.widgetConfig ? JSON.stringify(dashboard.widgetConfig, null, 2) : "",
      organizationIds: getDashboardOrgIds(dashboard),
      userIds: getDashboardUserIds(dashboard),
      tickers: Array.isArray(dashboard.tickers) ? dashboard.tickers : [],
      sectors,
      industries,
      sectionItems: sectionNodesFromApi(dashboard.sections),
      keywords,
      isCorporateAssigned: coerceApiBoolean(dashboard.isCorporateAssigned),
      showInMyDashboards: readShowInMyDashboards(dashboard),
      organizationsMeta: normalizeDashboardOrganizationsFromApi(dashboard.organizations),
      usersMeta: normalizeDashboardUsersFromApi(dashboard.users),
      refresh_frequency_hours:
        staleness.refresh_frequency_hours != null
          ? String(staleness.refresh_frequency_hours)
          : "",
      last_refreshed_at: staleness.last_refreshed_at,
      next_refresh_at: staleness.next_refresh_at,
      nextDueManual: false,
    });
    setKeywordsInput(formatKeywordsForInput(keywords));
    setKeywordsStatus("");
    setSavingError(null);
    setActiveTab("create");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    resetForm();
    setActiveTab("list");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSavingError(null);

    if (formMode === "create") {
      setConfirmAction({ type: "saveCreate" });
    } else if (formMode === "edit") {
      setConfirmAction({ type: "saveEdit" });
    }
  };

  const performSave = async (mode) => {
    setSaving(true);
    setSavingError(null);

    let parsedConfig = undefined;
    if (formValues.type === "tradingview" && formValues.widgetConfig) {
      try {
        parsedConfig = JSON.parse(formValues.widgetConfig);
      } catch (err) {
        try {
          // Fallback to forgiving JS object evaluation (handles trailing commas, etc)
          // eslint-disable-next-line no-new-func
          parsedConfig = new Function("return " + formValues.widgetConfig.trim())();
        } catch (evalErr) {
          setSavingError("Invalid JSON in Widget Config. Please check the format.");
          setSaving(false);
          setConfirmAction(null);
          return;
        }
      }

      // Helper to convert "true", "false", and "[]" strings into actual booleans/arrays
      if (parsedConfig && typeof parsedConfig === 'object') {
        const cleanupTypes = (obj) => {
          for (let key in obj) {
            if (obj[key] === "true") obj[key] = true;
            else if (obj[key] === "false") obj[key] = false;
            else if (obj[key] === "[]") obj[key] = [];
            else if (typeof obj[key] === 'object' && obj[key] !== null) {
              cleanupTypes(obj[key]);
            }
          }
        };
        cleanupTypes(parsedConfig);
      }
    }

    const organizationIds = Array.isArray(formValues.organizationIds)
      ? formValues.organizationIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
      : [];

    const userIds = Array.isArray(formValues.userIds)
      ? formValues.userIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
      : [];

    const sections = buildDashboardSectionsRequestBody(formValues.sectionItems || []);
    const keywords = parseKeywordsInput(keywordsInput);

    const githubLinkTrimmed = (formValues.githubLink || "").trim();
    const payload = {
      type: formValues.type,
      link: formValues.link,
      githubLink: githubLinkTrimmed || null,
      widgetConfig: formValues.type === "tradingview" ? parsedConfig : null,
      category: formValues.category,
      title: formValues.title,
      description: formValues.description ?? "",
      organizationIds,
      userIds,
      tickers: Array.isArray(formValues.tickers) ? formValues.tickers : [],
      sectors: mergeDashboardSectorPayload(formValues.sectors, formValues.industries),
      sections,
      ...showInMyDashboardsPayload(formValues.showInMyDashboards),
      ...refreshFrequencyPayload(mode, formValues.refresh_frequency_hours),
      // Manually recorded staleness times. Anything left blank on create falls back
      // to the backend (last_refreshed_at = now, next_refresh_at = now + frequency).
      ...stalenessTimestampsPayload(mode, {
        lastRefreshedAt: formValues.last_refreshed_at,
        nextRefreshAt: formValues.next_refresh_at,
        frequencyHours: formValues.refresh_frequency_hours,
      }),
    };

    // Create: only send keywords when provided — omit so backend LLM fills from title+description.
    // Edit: always send keywords so manual updates/clears persist.
    if (mode === "edit" || keywords.length > 0) {
      payload.keywords = keywords;
    }

    try {
      if (mode === "create") {
        await axios.post(
          endpoints.agentsDashboards,
          { ...payload, favourite: false },
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
        await refreshAgentCache();
      } else if (mode === "edit" && formValues.id != null) {
        await axios.patch(urlAgentsDashboard(formValues.id), payload, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });
        await refreshAgentCache();
      }
      resetForm();
      setActiveTab("list");
      setRefreshToken((t) => t + 1);
    } catch (err) {
      console.error("Error saving dashboard", err);
      setSavingError(getErrorMessage(err, mode === "create" ? "Failed to create dashboard" : "Failed to update dashboard"));
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const handleDelete = (dashboard) => {
    if (!dashboard?.id) return;
    setDeleteConfirmText("");
    setConfirmAction({ type: "delete", dashboard });
  };

  const isDeleteConfirmed = deleteConfirmText.trim().toLowerCase() === "delete";

  const handleDeleteCancel = () => {
    if (deleting) return;
    setConfirmAction(null);
    setDeleteConfirmText("");
  };

  /** Pushes backend-owned timestamps into the staleness table and the open form. */
  const applyRefreshTimestamps = (dashboardId, times) => {
    if (!times) return;
    // Both casings, so the camelCase values the API sent can't shadow the new ones.
    const patch = {
      ...times,
      lastRefreshedAt: times.last_refreshed_at,
      nextRefreshAt: times.next_refresh_at,
    };
    setStalenessData((prev) =>
      prev.map((d) => (String(d.id) === String(dashboardId) ? { ...d, ...patch } : d))
    );
    setFormValues((prev) =>
      String(prev.id) === String(dashboardId)
        ? { ...prev, ...patch, nextDueManual: false }
        : prev
    );
  };

  /**
   * `POST /agents/dashboards/:id/refresh` marks the dashboard as refreshed: the
   * backend stamps last refreshed as now and derives next due from the frequency.
   * The response's timestamps go straight into the table and the open form; when
   * it echoes none, the admin list is re-read instead of guessing the times here.
   */
  const handleRefreshDashboard = async (dashboard, { from = "table" } = {}) => {
    if (!dashboard?.id) return;
    setRefreshingDashboards((prev) => ({ ...prev, [dashboard.id]: true }));
    if (from === "form") setSavingError(null);
    try {
      const response = await axios.post(
        urlAdminRefreshDashboard(dashboard.id),
        manualRefreshPayload(dashboard),
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      let times = readRefreshTimestamps(response?.data);
      if (!times) {
        const all = await fetchAllDashboardsAdmin();
        times = readRefreshTimestamps(
          all.find((d) => String(d.id) === String(dashboard.id))
        );
      }
      if (!times) {
        throw new Error("The API did not return a refresh time for this dashboard.");
      }
      applyRefreshTimestamps(dashboard.id, times);
    } catch (err) {
      console.error("Error refreshing dashboard", err);
      const message = getErrorMessage(err, "Failed to record the refresh");
      if (from === "form") setSavingError(message);
      else setError(message);
    } finally {
      setRefreshingDashboards((prev) => ({ ...prev, [dashboard.id]: false }));
    }
  };

  const [thumbnailDashboard, setThumbnailDashboard] = useState(null);
  const handleOpenThumbnailModal = (dashboard) => {
    if (!dashboard?.id) return;
    setThumbnailDashboard(dashboard);
  };
  const handleCloseThumbnailModal = () => setThumbnailDashboard(null);

  const applyKeywordsToForm = (keywords) => {
    const normalized = normalizeDashboardKeywords(keywords);
    setFormValues((prev) => ({ ...prev, keywords: normalized }));
    setKeywordsInput(formatKeywordsForInput(normalized));
  };

  const handleGenerateKeywords = async () => {
    if (formMode !== "edit" || formValues.id == null) {
      setSavingError("Save the dashboard first, then generate keywords from edit.");
      return;
    }

    setExtractingKeywords(true);
    setSavingError(null);
    setKeywordsStatus("");
    try {
      const response = await axios.post(
        endpoints.dashboardExtractKeywords,
        { dashboard_id: Number(formValues.id) },
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );
      const extracted = keywordsFromDashboardResponse(response.data);
      if (extracted.length > 0) {
        applyKeywordsToForm(extracted);
        setKeywordsStatus("Keywords updated. You can edit them and Save if needed.");
      } else {
        // Extract may persist without returning keywords — refresh from admin list.
        const { data } = await fetchDashboardsAdminPage({
          page: 1,
          limit: DASHBOARDS_ADMIN_MAX_LIMIT,
          search: formValues.title || undefined,
        });
        const match = data.find((d) => Number(d.id) === Number(formValues.id));
        if (match && normalizeDashboardKeywords(match.keywords).length > 0) {
          applyKeywordsToForm(match.keywords);
          setKeywordsStatus("Keywords updated. You can edit them and Save if needed.");
        } else {
          setKeywordsStatus(
            "Please wait a few minutes for processing — keywords will be auto-updated once processed. You can save and come back later."
          );
        }
      }
      setRefreshToken((t) => t + 1);
    } catch (err) {
      console.error("Error generating dashboard keywords", err);
      setSavingError(getErrorMessage(err, "Failed to generate keywords"));
    } finally {
      setExtractingKeywords(false);
    }
  };

  const addSection = () => {
    setFormValues((prev) => ({
      ...prev,
      sectionItems: [...(prev.sectionItems || []), createSectionNode()],
    }));
  };

  const updateSectionNodeName = (nodeId, name) => {
    setFormValues((prev) => ({
      ...prev,
      sectionItems: updateNodeInTree(prev.sectionItems, nodeId, (node) => ({ ...node, name })),
    }));
  };

  const removeSectionNode = (nodeId) => {
    setFormValues((prev) => ({
      ...prev,
      sectionItems: removeNodeFromTree(prev.sectionItems, nodeId),
    }));
  };

  const addChildSectionNode = (parentId) => {
    setFormValues((prev) => ({
      ...prev,
      sectionItems: addChildToNode(prev.sectionItems, parentId, createSectionNode()),
    }));
  };

  const sectionsPayloadPreview = useMemo(
    () => formatDashboardSectionsPayloadPreview(formValues.sectionItems || []),
    [formValues.sectionItems]
  );

  const sectionsLadderLines = useMemo(
    () => buildSectionLadderLines(formValues.sectionItems || []),
    [formValues.sectionItems]
  );

  const sectionCount = (formValues.sectionItems || []).length;
  const nextTopSectionLabel =
    sectionCount === 0 ? "Add Section 1" : `+ Section ${sectionCount + 1}`;

  const addTopSectionButtonStyle = {
    ...addButtonStyle,
    marginTop: sectionCount > 0 ? "12px" : 0,
    width: "auto",
    minWidth: sectionCount === 0 ? "100%" : undefined,
    padding: sectionCount === 0 ? "10px 14px" : "0 12px",
    fontSize: sectionCount === 0 ? "14px" : "13px",
    borderStyle: sectionCount === 0 ? "dashed" : "solid",
    alignSelf: sectionCount === 0 ? "stretch" : "flex-start",
  };

  const renderSectionEditorNodes = (nodes, indexPath = []) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    const depth = indexPath.length + 1;
    const canAddChild = depth < MAX_SECTION_DEPTH;

    return nodes.map((node, index) => {
      const currentPath = [...indexPath, index];
      const badge = formatSectionBadge(currentPath);

      return (
      <div
        key={node.id}
        style={{
          ...sectionRowStyle,
          marginLeft: depth > 1 ? `${(depth - 1) * 16}px` : 0,
          borderLeft: depth > 1 ? "2px solid #c7d2fe" : undefined,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: "#3730a3",
              backgroundColor: "#eef2ff",
              borderRadius: "999px",
              padding: "4px 8px",
              letterSpacing: "0.02em",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {badge}
          </span>
          <input
            type="text"
            placeholder={depth === 1 ? "Section name" : "Subsection name"}
            value={node.name}
            onChange={(e) => updateSectionNodeName(node.id, e.target.value)}
            style={{
              flex: 1,
              padding: depth === 1 ? "9px 10px" : "8px 10px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              fontSize: depth === 1 ? "14px" : "13px",
              backgroundColor: depth === 1 ? "#ffffff" : "#f9fafb",
            }}
            aria-label={`${badge} name`}
          />
          <button
            type="button"
            onClick={() => removeSectionNode(node.id)}
            style={removeButtonStyle}
            aria-label="Remove node"
            title="Remove"
          >
            ×
          </button>
        </div>
        {Array.isArray(node.children) && node.children.length > 0 && (
          <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {renderSectionEditorNodes(node.children, currentPath)}
          </div>
        )}
        {canAddChild && (
          <button
            type="button"
            onClick={() => addChildSectionNode(node.id)}
            style={{
              ...addButtonStyle,
              borderStyle: "dashed",
              marginTop: "8px",
              width: "auto",
              padding: "0 10px",
              fontSize: "13px",
            }}
            aria-label={`Add sub ${currentPath.map((i) => i + 1).join(".")}.X under ${node.name || badge}`}
            title={`Add ${formatSectionBadge([...currentPath, (node.children || []).length])}`}
          >
            + {formatSectionBadge([...currentPath, (node.children || []).length])}
          </button>
        )}
      </div>
    );
    });
  };

  /** Only reachable once "delete" has been typed in the confirm modal. */
  const performDelete = async (dashboard) => {
    if (!isDeleteConfirmed || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(urlAgentsDashboard(dashboard.id), {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });
      await refreshAgentCache();
      if (formMode === "edit" && formValues.id === dashboard.id) {
        resetForm();
      }
      setRefreshToken((t) => t + 1);
    } catch (err) {
      console.error("Error deleting dashboard", err);
      setError(getErrorMessage(err, "Failed to delete dashboard"));
    } finally {
      setDeleting(false);
      setDeleteConfirmText("");
      setConfirmAction(null);
    }
  };

  const handleOrgVisibilityHeaderEdit = async () => {
    if (loading || orgVisibilityEditable || savingOrgVisibility) return;
    setLoading(true);
    setError(null);
    try {
      const all = await fetchAllDashboardsAdmin();
      if (all.length === 0) return;
      setAllDashboardsForOrgEdit(all);
      const initial = {};
      all.forEach((d) => {
        initial[d.id] = getDashboardOrgIds(d);
      });
      setOrgVisibilityValues(initial);
      setOrgVisibilityEditable(true);
    } catch (err) {
      console.error("Error loading dashboards for org visibility edit", err);
      setError(getErrorMessage(err, "Failed to load dashboards for org visibility edit"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrgVisibility = () => {
    if (savingOrgVisibility) return;
    setOrgVisibilityEditable(false);
    setOrgVisibilityValues({});
    setAllDashboardsForOrgEdit([]);
  };

  const handleSaveOrgVisibility = async () => {
    setSavingOrgVisibility(true);
    setError(null);
    let success = 0;
    let failed = 0;
    const source = allDashboardsForOrgEdit;
    const toPatch = source.filter((d) => {
      const original = getDashboardOrgIds(d);
      const current = orgVisibilityValues[d.id] ?? original;
      return !areSameIds(current, original);
    });
    for (const d of toPatch) {
      const current = orgVisibilityValues[d.id] ?? getDashboardOrgIds(d);
      const organizationIds = [...current].map((id) => Number(id)).filter((n) => !Number.isNaN(n));
      try {
        await axios.patch(
          urlAgentsDashboard(d.id),
          { organizationIds },
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
        await refreshAgentCache();
        success += 1;
      } catch (err) {
        console.error(`Dashboard org visibility PATCH failed: ${d.id}`, err);
        failed += 1;
      }
    }
    setSavingOrgVisibility(false);
    setOrgVisibilityEditable(false);
    setOrgVisibilityValues({});
    setAllDashboardsForOrgEdit([]);
    if (failed > 0) {
      setError(
        success > 0
          ? `Saved ${success} dashboard(s). ${failed} update(s) failed — see console for details.`
          : "Could not save organisation visibility. See console for details."
      );
    }
    if (success > 0) {
      setRefreshToken((t) => t + 1);
    }
  };

  const handleUserVisibilityHeaderEdit = async () => {
    if (loading || userVisibilityEditable || savingUserVisibility) return;
    setLoading(true);
    setError(null);
    try {
      const all = await fetchAllDashboardsAdmin();
      if (all.length === 0) return;
      if (orgUsersForPicker.length === 0) {
        const groups = await fetchAllOrgUsers(endpoints.dashboardsOrgUsers, { limit: 100 });
        setOrgUsersForPicker(flattenOrgUsersForPicker(groups));
      }
      setAllDashboardsForUserEdit(all);
      const initial = {};
      all.forEach((d) => {
        initial[d.id] = getDashboardUserIds(d);
      });
      setUserVisibilityValues(initial);
      setUserVisibilityEditable(true);
    } catch (err) {
      console.error("Error loading dashboards for user visibility edit", err);
      setError(getErrorMessage(err, "Failed to load dashboards for user visibility edit"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUserVisibility = () => {
    if (savingUserVisibility) return;
    setUserVisibilityEditable(false);
    setUserVisibilityValues({});
    setAllDashboardsForUserEdit([]);
  };

  const handleSaveUserVisibility = async () => {
    setSavingUserVisibility(true);
    setError(null);
    let success = 0;
    let failed = 0;
    const source = allDashboardsForUserEdit;
    const toPatch = source.filter((d) => {
      const original = getDashboardUserIds(d);
      const current = userVisibilityValues[d.id] ?? original;
      return !areSameIds(current, original);
    });
    for (const d of toPatch) {
      const current = userVisibilityValues[d.id] ?? getDashboardUserIds(d);
      const userIds = [...current].map((id) => Number(id)).filter((n) => !Number.isNaN(n));
      try {
        await axios.patch(
          urlAgentsDashboard(d.id),
          { userIds },
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
        await refreshAgentCache();
        success += 1;
      } catch (err) {
        console.error(`Dashboard user visibility PATCH failed: ${d.id}`, err);
        failed += 1;
      }
    }
    setSavingUserVisibility(false);
    setUserVisibilityEditable(false);
    setUserVisibilityValues({});
    setAllDashboardsForUserEdit([]);
    if (failed > 0) {
      setError(
        success > 0
          ? `Saved ${success} dashboard(s). ${failed} update(s) failed — see console for details.`
          : "Could not save user visibility. See console for details."
      );
    }
    if (success > 0) {
      setRefreshToken((t) => t + 1);
    }
  };

  const visibleDashboards = (() => {
    // "Visible to user" is resolved over the whole list, so it pages locally.
    if (userVisibleDashboards) {
      const start = (listPage - 1) * DASHBOARDS_ADMIN_DEFAULT_LIMIT;
      return userVisibleDashboards.slice(start, start + DASHBOARDS_ADMIN_DEFAULT_LIMIT);
    }

    const base = orgVisibilityEditable
      ? allDashboardsForOrgEdit
      : isOrgVisibilityView && orgVisibilityScope !== "all"
      ? orgScopeAllDashboards
      : userVisibilityEditable
      ? allDashboardsForUserEdit
      : dashboards;

    if (!isOrgVisibilityView || orgVisibilityScope === "all") return base;

    return base.filter((d) => {
      const orgIds =
        orgVisibilityEditable && orgVisibilityValues[d.id] != null
          ? orgVisibilityValues[d.id]
          : getDashboardOrgIds(d);
      const isAllUsers = !Array.isArray(orgIds) || orgIds.length === 0;
      if (orgVisibilityScope === "all_users") return isAllUsers;
      if (orgVisibilityScope === "customers") return !isAllUsers;
      return true;
    });
  })();
  const visibleStaleness = useMemo(() => {
    let result = stalenessData;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (d) =>
          (d.title && d.title.toLowerCase().includes(q)) ||
          (d.category && d.category.toLowerCase().includes(q))
      );
    }
    if (filterOverdueOnly) {
      result = result.filter((d) => isDashboardOverdue(d));
    }
    return result;
  }, [stalenessData, debouncedSearch, filterOverdueOnly]);

  const overdueCount = useMemo(
    () => stalenessData.filter((d) => isDashboardOverdue(d)).length,
    [stalenessData]
  );

  /** Row count the list footer and pager work off — local when a user is selected. */
  const listTotal = userVisibleDashboards ? userVisibleDashboards.length : totalDashboards;
  const totalPages = Math.max(1, Math.ceil(listTotal / DASHBOARDS_ADMIN_DEFAULT_LIMIT));
  const hasOtherListFilters =
    Boolean(debouncedSearch) ||
    Boolean(debouncedCategory) ||
    filterTickers.length > 0 ||
    filterSectors.length > 0 ||
    filterIndustries.length > 0 ||
    filterFavourite ||
    filterCorporateView;
  const hasListFilters = hasOtherListFilters || userFilterActive;

  // A narrowed result set can leave the pager past the last page.
  useEffect(() => {
    if (listPage > totalPages) setListPage(totalPages);
  }, [listPage, totalPages]);

  const listStart = listTotal === 0 ? 0 : (listPage - 1) * DASHBOARDS_ADMIN_DEFAULT_LIMIT + 1;
  const listEnd = orgVisibilityEditable || userVisibilityEditable || (isOrgVisibilityView && orgVisibilityScope !== "all")
    ? visibleDashboards.length
    : Math.min(listPage * DASHBOARDS_ADMIN_DEFAULT_LIMIT, listTotal);

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <style>
        {`@keyframes dashboardsShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }`}
      </style>
      {onClose && (
        <button style={closeButtonStyle} type="button" onClick={onClose}>
          Close
        </button>
      )}
      <h2 style={titleStyle}>Dashboards</h2>

      <nav style={tabBarStyle} aria-label="Dashboard sections">
        {DASHBOARD_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            style={tabButtonStyle(activeTab === tab.id)}
            onClick={() => handleTabChange(tab.id)}
            aria-current={activeTab === tab.id ? "page" : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {isOrgVisibilityView && (
        <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#4b5563", maxWidth: "720px" }}>
          Manage which organisations can see each dashboard. Use Edit to bulk-update org visibility, then save.
        </p>
      )}

      {isUserVisibilityView && (
        <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#4b5563", maxWidth: "720px" }}>
          Manage which users can see each dashboard (by user index). Empty selection means no user-level restriction.
          Users can still access via org rules. Use Edit to bulk-update, then save.
        </p>
      )}

      {isCreateTab && (
      <form onSubmit={handleSubmit} style={{ marginBottom: "24px", maxWidth: "960px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", color: "#111827" }}>
            {formMode === "create" ? "Add Dashboard" : "Edit Dashboard"}
          </h3>
        </div>
        {savingError && (
          <p style={{ color: "#b91c1c", marginBottom: "8px" }}>{savingError}</p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <input
            type="text"
            required
            placeholder="Title"
            value={formValues.title}
            onChange={(e) => handleFormChange("title", e.target.value)}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label htmlFor="dashboard-category" style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Category
            </label>
            <input
              id="dashboard-category"
              type="text"
              required
              list="dashboard-category-list"
              placeholder="Select or type a new category"
              value={formValues.category}
              onChange={(e) => handleFormChange("category", e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
            <datalist id="dashboard-category-list">
              {distinctCategories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Dashboard Type
            </label>
            <select
              value={formValues.type}
              onChange={(e) => handleFormChange("type", e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                backgroundColor: "white"
              }}
            >
              <option value="iframe">URL Embed (Iframe)</option>
              <option value="tradingview">TradingView Widget (JSON)</option>
            </select>
          </div>
          <input
            type="url"
            required={formValues.type === "iframe"}
            placeholder={formValues.type === "iframe" ? "Link (https://...)" : "Script URL (optional, e.g., https://s3.tradingview.com/tv.js)"}
            value={formValues.link}
            onChange={(e) => handleFormChange("link", e.target.value)}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label htmlFor="dashboard-github-link" style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              GitHub link (optional)
            </label>
            <input
              id="dashboard-github-link"
              type="url"
              placeholder="https://github.com/org/repo"
              value={formValues.githubLink}
              onChange={(e) => handleFormChange("githubLink", e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>
          {formValues.type === "tradingview" && (
            <textarea
              required
              placeholder='{"widgetType": "advanced-chart", "symbol": "NASDAQ:AAPL", ...}'
              value={formValues.widgetConfig}
              onChange={(e) => handleFormChange("widgetConfig", e.target.value)}
              rows={8}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                resize: "vertical",
                fontFamily: "monospace",
              }}
            />
          )}
          <textarea
            placeholder="Description (optional)"
            value={formValues.description}
            onChange={(e) => handleFormChange("description", e.target.value)}
            rows={3}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Organisation visibility
            </label>
            <div style={{ maxWidth: 480 }}>
              <MultiSelectDropdown
                options={buildOrgMultiSelectOptions(availableOrgs, formValues.organizationIds || [])}
                value={(formValues.organizationIds || []).map((id) => String(id))}
                onChange={(vals) => handleFormChange("organizationIds", parseOrgIdsFromMultiSelectValues(vals))}
                placeholder="Nothing selected = no org restriction"
                searchable
                optionColumns={2}
                aria-label="Dashboard organisation visibility"
              />
            </div>
            {(formValues.organizationIds || []).length > 0 && (
              <div style={assigneeChipRowStyle}>
                {(formValues.organizationIds || []).map((orgId) => {
                  const meta = (formValues.organizationsMeta || []).find(
                    (org) => Number(org.organization_id ?? org.id) === Number(orgId)
                  );
                  const fromApi = availableOrgs.find((o) => Number(o.id) === Number(orgId));
                  const name = meta?.name || meta?.org_name || fromApi?.name || `Org ${orgId}`;
                  const corporate = orgIdHasCorporateUsers(
                    orgId,
                    formValues.organizationsMeta,
                    orgUsersForPicker
                  );
                  return (
                    <span key={orgId} style={assigneeChipStyle}>
                      {name}
                      {corporate && <span style={corporateMiniChipStyle}>Corporate</span>}
                    </span>
                  );
                })}
              </div>
            )}
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Empty = no org restriction. Org members can access. Combined with user visibility below (OR logic).
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              User visibility
            </label>
            <div style={{ maxWidth: 480 }}>
              <MultiSelectDropdown
                options={buildUserMultiSelectOptions(orgUsersForPicker, formValues.userIds || [])}
                value={(formValues.userIds || []).map((id) => String(id))}
                onChange={(vals) => handleFormChange("userIds", parseUserIdsFromMultiSelectValues(vals))}
                placeholder="Nothing selected = no user restriction"
                searchable
                optionColumns={2}
                aria-label="Dashboard user visibility"
              />
            </div>
            {(formValues.userIds || []).length > 0 && (
              <div style={assigneeChipRowStyle}>
                {(formValues.userIds || []).map((userId) => {
                  const meta = (formValues.usersMeta || []).find(
                    (user) => Number(user.user_id) === Number(userId)
                  );
                  const fromPicker = orgUsersForPicker.find(
                    (user) => Number(user.userIndex) === Number(userId)
                  );
                  const name =
                    meta?.name ||
                    meta?.email ||
                    fromPicker?.name ||
                    fromPicker?.email ||
                    `User ${userId}`;
                  const corporate =
                    isCorporateUserType(meta?.user_type) ||
                    isCorporateUserType(fromPicker?.userType);
                  return (
                    <span key={userId} style={assigneeChipStyle}>
                      {name}
                      {corporate && <span style={corporateMiniChipStyle}>Corporate</span>}
                    </span>
                  );
                })}
              </div>
            )}
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Optional. Select specific users by user index. Listed users can access even if their org is not selected above.
            </p>
            {resolveIsCorporateAssigned({
              isCorporateAssigned: formValues.isCorporateAssigned,
              organizations: formValues.organizationsMeta,
              users: formValues.usersMeta,
              organizationIds: formValues.organizationIds,
              userIds: formValues.userIds,
              pickerUsers: orgUsersForPicker,
            }) && (
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#0f766e",
                  backgroundColor: "#f0fdfa",
                  border: "1px solid #99f6e4",
                  borderRadius: "6px",
                  padding: "8px 10px",
                  lineHeight: 1.45,
                }}
              >
                Corporate users only see this dashboard if assigned (org and/or user). They never see public
                dashboards. Selecting an org that contains corporate users also counts as a corporate
                assignment.
              </p>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label
              htmlFor="dashboard-show-in-my-dashboards"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                color: "#374151",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              <input
                id="dashboard-show-in-my-dashboards"
                type="checkbox"
                checked={coerceShowInMyDashboards(formValues.showInMyDashboards)}
                onChange={(e) => handleFormChange("showInMyDashboards", e.target.checked)}
              />
              Show in My Dashboards
            </label>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Off by default. When on, this dashboard also appears in the user's My Dashboards
              section. Visibility still applies — only users who can see the dashboard get it there.
            </p>
          </div>
          <div
            style={{
              backgroundColor: "#eff6ff",
              border: "1px solid #93c5fd",
              borderRadius: "8px",
              padding: "14px 16px",
              marginBottom: "4px",
              color: "#1e3a8a",
              fontSize: "13px",
              lineHeight: 1.6,
            }}
          >
            <strong>Dashboard context type</strong>
            <p style={{ margin: "8px 0 0" }}>
              A dashboard runs in one of two context modes — choose how it should appear to users:
            </p>
            <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
              <li>
                <strong>Ticker context</strong> — select tickers below. The dashboard is shown when the user has
                one of those tickers selected. Sector and industry fields autofill from the stock search mapping
                for convenience.
              </li>
              <li>
                <strong>Sector / industry context</strong> — leave tickers empty and set sectors and/or industries.
                The dashboard is shown when the user is browsing that sector or industry.
              </li>
            </ul>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Tickers
            </label>
            <div style={{ maxWidth: 480 }}>
              <MultiSelectDropdown
                options={availableTickers}
                value={formValues.tickers || []}
                onChange={handleTickersChange}
                placeholder="Nothing Selected means Shown to All"
                searchable
                optionColumns={1}
                aria-label="Dashboard Tickers"
              />
            </div>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Ticker context: if empty, the dashboard is shown for all tickers. Selecting a ticker autofills its
              mapped sector and industry from stock search.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Sectors
            </label>
            <div style={{ maxWidth: 480 }}>
              <MultiSelectDropdown
                options={availableSectorOptions}
                value={formValues.sectors || []}
                onChange={(vals) => handleFormChange("sectors", vals)}
                placeholder="Nothing Selected means Shown to All"
                searchable
                optionColumns={1}
                aria-label="Dashboard Sectors"
              />
            </div>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Sector context: if empty with no tickers, shown for all sectors. Autofilled when tickers are selected;
              you can edit manually.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Industries
            </label>
            <div style={{ maxWidth: 480 }}>
              <MultiSelectDropdown
                options={availableIndustryOptions}
                value={formValues.industries || []}
                onChange={(vals) => handleFormChange("industries", vals)}
                placeholder="Nothing Selected means Shown to All"
                searchable
                optionColumns={1}
                aria-label="Dashboard Industries"
              />
            </div>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Sector context: if empty with no tickers, shown for all industries. Autofilled when tickers are
              selected; you can edit manually.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
              Sections (optional, up to {MAX_SECTION_DEPTH} levels)
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 280px)",
                gap: "12px",
                alignItems: "start",
              }}
            >
              <div style={sectionPanelStyle}>
                {sectionCount > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {renderSectionEditorNodes(formValues.sectionItems || [])}
                  </div>
                )}
                <button
                  type="button"
                  onClick={addSection}
                  style={addTopSectionButtonStyle}
                  aria-label={nextTopSectionLabel}
                  title={nextTopSectionLabel}
                >
                  {nextTopSectionLabel}
                </button>
              </div>
              <div
                style={{
                  border: "1px solid #dbe3f4",
                  borderRadius: "12px",
                  padding: "10px",
                  backgroundColor: "#0f172a",
                  color: "#e2e8f0",
                  minHeight: "120px",
                  maxHeight: "360px",
                  overflow: "auto",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: "11px",
                  lineHeight: 1.5,
                }}
                aria-label="Section hierarchy preview"
              >
                <div style={{ color: "#94a3b8", fontSize: "10px", marginBottom: "8px", letterSpacing: "0.04em" }}>
                  HIERARCHY PREVIEW
                </div>
                {sectionsLadderLines.length === 0 ? (
                  <span style={{ color: "#64748b" }}>—</span>
                ) : (
                  sectionsLadderLines.map((line, i) => (
                    <div key={`${line}-${i}`} style={{ whiteSpace: "pre" }}>
                      {line}
                    </div>
                  ))
                )}
                <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #334155", color: "#94a3b8", fontSize: "10px" }}>
                  API payload (nested JSON)
                </div>
                <pre style={{ margin: "6px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#cbd5e1" }}>
                  {sectionsPayloadPreview}
                </pre>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Click Add Section 1 to start. Each + Section N below adds another top-level section. Use + Sub X.X… under a row to nest.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }} htmlFor="dashboard-keywords">
              Keywords
            </label>
            <textarea
              id="dashboard-keywords"
              value={keywordsInput}
              onChange={(e) => {
                const next = e.target.value;
                setKeywordsInput(next);
                setFormValues((prev) => ({ ...prev, keywords: parseKeywordsInput(next) }));
              }}
              placeholder={
                formMode === "create"
                  ? "Leave blank — auto-generated after create"
                  : "equity, markets, stocks"
              }
              rows={3}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
            {formMode === "create" ? (
              <p style={{ margin: 0, fontSize: "12px", color: "#6b7280", lineHeight: 1.45 }}>
                Leave blank and save — keywords will be auto-generated internally. Please wait a few minutes for
                processing; they will be auto-updated once processed. You can also enter keywords manually here if
                you want them stored immediately.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={handleGenerateKeywords}
                    disabled={extractingKeywords || saving || formValues.id == null}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #3730a3",
                      backgroundColor: "#eef2ff",
                      color: "#3730a3",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: extractingKeywords || saving || formValues.id == null ? "not-allowed" : "pointer",
                      opacity: extractingKeywords || saving || formValues.id == null ? 0.65 : 1,
                    }}
                  >
                    {extractingKeywords ? "Generating…" : "Generate keywords"}
                  </button>
                  <span style={{ fontSize: "12px", color: "#6b7280", lineHeight: 1.45, flex: "1 1 220px" }}>
                    {extractingKeywords
                      ? "Please wait a few minutes for processing — keywords will be auto-updated once processed. You can save and come back later."
                      : "Generate via extract-keywords, or edit manually and Save."}
                  </span>
                </div>
                {keywordsStatus && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "#1d4ed8",
                      lineHeight: 1.45,
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "6px",
                      padding: "8px 10px",
                    }}
                  >
                    {keywordsStatus}
                  </p>
                )}
              </div>
            )}
            {(formValues.keywords || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {formValues.keywords.map((kw) => (
                  <span
                    key={kw}
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      backgroundColor: "#e0e7ff",
                      color: "#3730a3",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
          {/* ── Data Staleness Management ── */}
          <div
            style={{
              marginTop: "4px",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid #c7d2fe",
              backgroundColor: "#f8faff",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#3730a3",
                }}
              >
                Data Staleness Management
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#6b7280" }}>
                Pick a refresh schedule and record when the data was last refreshed. Sent with the
                dashboard on save and shown in the Data Staleness tab.
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
              <div style={{ flex: "1 1 220px", minWidth: "200px" }}>
                <label htmlFor="dashboard-refresh-frequency" style={stalenessFieldLabelStyle}>
                  Refresh frequency
                </label>
                <select
                  id="dashboard-refresh-frequency"
                  value={
                    formValues.refresh_frequency_hours == null
                      ? ""
                      : String(formValues.refresh_frequency_hours)
                  }
                  onChange={(e) =>
                    applyStalenessChange({ refresh_frequency_hours: e.target.value })
                  }
                  style={{ ...stalenessInputStyle, cursor: "pointer" }}
                >
                  {refreshFrequencyOptions(formValues.refresh_frequency_hours).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div style={stalenessFieldHintStyle}>
                  How often this dashboard's data should be refreshed.
                </div>
              </div>

              <StalenessDateField
                id="dashboard-last-refreshed"
                label="Last refreshed"
                required
                warn={missingLastRefreshed}
                value={toDateTimeInputValue(formValues.last_refreshed_at)}
                onChange={handleLastRefreshedChange}
                hint={
                  missingLastRefreshed
                    ? "Required — please fill this in (not enforced; leaving it empty lets the backend stamp the save time)."
                    : "A manual refresh you did counts as the last refresh — set it here."
                }
              >
                <button
                  type="button"
                  onClick={handleMarkRefreshedToday}
                  style={{
                    marginTop: "6px",
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "1px solid #c7d2fe",
                    backgroundColor: "#eef2ff",
                    color: "#3730a3",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  title="Set last refreshed to now and re-derive next due"
                >
                  Refreshed today
                </button>
              </StalenessDateField>

              <StalenessDateField
                id="dashboard-next-due"
                label="Next due"
                value={toDateTimeInputValue(formValues.next_refresh_at)}
                onChange={handleNextDueChange}
                hint={
                  formValues.nextDueManual
                    ? "Set by hand — saved as entered. Clear it to go back to last refreshed + frequency."
                    : formValues.next_refresh_at
                    ? "Follows last refreshed + frequency. Edit to override."
                    : "Fills in from last refreshed + frequency, or type one."
                }
              />
            </div>

            {formMode === "edit" && formValues.id != null && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={
                    !formValues.refresh_frequency_hours ||
                    Boolean(refreshingDashboards[formValues.id])
                  }
                  onClick={() =>
                    handleRefreshDashboard(
                      {
                        id: formValues.id,
                        title: formValues.title,
                        refresh_frequency_hours: formValues.refresh_frequency_hours,
                      },
                      { from: "form" }
                    )
                  }
                  style={{
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #c7d2fe",
                    backgroundColor: "#fff",
                    color: "#3730a3",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor:
                      !formValues.refresh_frequency_hours ||
                      refreshingDashboards[formValues.id]
                        ? "not-allowed"
                        : "pointer",
                    opacity: !formValues.refresh_frequency_hours ? 0.55 : 1,
                  }}
                  title={
                    !formValues.refresh_frequency_hours
                      ? "Pick a refresh frequency first"
                      : "Mark as refreshed now"
                  }
                >
                  {refreshingDashboards[formValues.id] ? "Refreshing…" : "↻ Refresh now"}
                </button>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  Refreshing now stamps last refreshed and recomputes next due.
                </span>
              </div>
            )}
          </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: "10px 18px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#3730a3",
              color: "#ffffff",
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "600",
              opacity: saving ? 0.8 : 1,
            }}
          >
            {saving
              ? formMode === "create"
                ? "Creating..."
                : "Saving..."
              : formMode === "create"
              ? "Create"
              : "Save changes"}
          </button>
          {formMode === "edit" && (
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={saving}
              style={{
                padding: "10px 18px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor: "#ffffff",
                color: "#374151",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Cancel edit
            </button>
          )}
        </div>
      </form>
      )}

      {!isCreateTab && !error && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "16px" }}>
            <div style={{ ...searchWrapperStyle, marginBottom: 0 }}>
              <span style={searchIconStyle} aria-hidden>
                ⌕
              </span>
              <input
                type="search"
                placeholder="Search title, description, category, link…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={searchInputStyle}
                aria-label="Search dashboards"
                disabled={(isOrgVisibilityView && orgVisibilityEditable) || (isUserVisibilityView && userVisibilityEditable)}
              />
            </div>
            {isListTab && (
              <div style={{ minWidth: "180px", maxWidth: "220px" }}>
                <input
                  type="text"
                  list="dashboard-list-category-filter"
                  placeholder="Filter category"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #3730a3",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                  aria-label="Filter by category"
                />
                <datalist id="dashboard-list-category-filter">
                  {distinctCategories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
            )}
            {isListTab && (
              <div style={{ minWidth: "200px", maxWidth: "280px" }}>
                <MultiSelectDropdown
                  options={availableTickers}
                  value={filterTickers}
                  onChange={(vals) => {
                    setFilterTickers(vals);
                    setListPage(1);
                  }}
                  placeholder="Filter tickers"
                  searchable
                  optionColumns={1}
                  aria-label="Filter dashboards by ticker"
                />
              </div>
            )}
            {isListTab && (
              <div style={{ minWidth: "200px", maxWidth: "280px" }}>
                <MultiSelectDropdown
                  options={availableSectorOptions}
                  value={filterSectors}
                  onChange={(vals) => {
                    setFilterSectors(vals);
                    setListPage(1);
                  }}
                  placeholder="Filter sectors"
                  searchable
                  optionColumns={1}
                  aria-label="Filter dashboards by sector"
                />
              </div>
            )}
            {isListTab && (
              <div style={{ minWidth: "200px", maxWidth: "280px" }}>
                <MultiSelectDropdown
                  options={availableIndustryOptions}
                  value={filterIndustries}
                  onChange={(vals) => {
                    setFilterIndustries(vals);
                    setListPage(1);
                  }}
                  placeholder="Filter industries"
                  searchable
                  optionColumns={1}
                  aria-label="Filter dashboards by industry"
                />
              </div>
            )}
            {isListTab && (
              <div style={{ minWidth: "240px", maxWidth: "320px" }}>
                <MultiSelectDropdown
                  options={userFilterOptions}
                  value={filterUserId ? [filterUserId] : []}
                  onChange={(vals) => {
                    // Single-select: the newest pick replaces the previous one.
                    const nextUserId = vals.length > 0 ? vals[vals.length - 1] : "";
                    setFilterUserId(nextUserId);
                    // Show the shimmer straight away — the effect refetches the
                    // full list before access can be resolved.
                    setLoadingUserScope(Boolean(nextUserId));
                    setListPage(1);
                  }}
                  placeholder="Visible to user…"
                  searchable
                  optionColumns={1}
                  aria-label="Show only dashboards visible to a user"
                />
              </div>
            )}
            {isListTab && (
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={filterFavourite}
                  onChange={(e) => {
                    setFilterFavourite(e.target.checked);
                    setListPage(1);
                  }}
                />
                Favourites only
              </label>
            )}
            {isListTab && (
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: filterCorporateView ? "#0f766e" : "#374151",
                  cursor: "pointer",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: filterCorporateView ? "1px solid #99f6e4" : "1px solid #e5e7eb",
                  backgroundColor: filterCorporateView ? "#f0fdfa" : "#ffffff",
                }}
                title="Show only dashboards with corporate assignees: corporate users, corporate orgs, or any org that has corporate users"
              >
                <input
                  type="checkbox"
                  checked={filterCorporateView}
                  onChange={(e) => {
                    setFilterCorporateView(e.target.checked);
                    setListPage(1);
                  }}
                />
                <FiLock size={14} aria-hidden />
                Corporate view
              </label>
            )}
            {isStalenessTab && (
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: filterOverdueOnly ? "#b91c1c" : "#374151",
                  cursor: "pointer",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: filterOverdueOnly ? "1px solid #fca5a5" : "1px solid #e5e7eb",
                  backgroundColor: filterOverdueOnly ? "#fef2f2" : "#ffffff",
                }}
              >
                <input
                  type="checkbox"
                  checked={filterOverdueOnly}
                  onChange={(e) => setFilterOverdueOnly(e.target.checked)}
                />
                Overdue only
                {overdueCount > 0 && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "20px",
                      height: "20px",
                      padding: "0 5px",
                      borderRadius: "999px",
                      backgroundColor: "#b91c1c",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  >
                    {overdueCount}
                  </span>
                )}
              </label>
            )}
            {isOrgVisibilityView && (
              <div style={{ minWidth: "220px" }}>
                <label htmlFor="org-visibility-scope" style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                  Visibility filter
                </label>
                <select
                  id="org-visibility-scope"
                  value={orgVisibilityScope}
                  onChange={(e) => setOrgVisibilityScope(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #3730a3",
                    fontSize: "14px",
                    backgroundColor: "#ffffff",
                    color: "#111827",
                    boxSizing: "border-box",
                  }}
                  aria-label="Filter by organisation visibility"
                >
                  <option value="all">All dashboards</option>
                  <option value="all_users">Shown to all users</option>
                  <option value="customers">Shown by customers (orgs)</option>
                </select>
              </div>
            )}
            {isOrgVisibilityView && orgVisibilityEditable && (
              <div style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
                <button
                  type="button"
                  onClick={handleSaveOrgVisibility}
                  disabled={savingOrgVisibility}
                  style={{ ...orgToolbarButtonStyle, backgroundColor: "#059669", color: "#fff" }}
                >
                  {savingOrgVisibility ? "Saving…" : "Save org visibility"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelOrgVisibility}
                  disabled={savingOrgVisibility}
                  style={{ ...orgToolbarButtonStyle, backgroundColor: "#6b7280", color: "#fff" }}
                >
                  Cancel
                </button>
              </div>
            )}
            {isUserVisibilityView && userVisibilityEditable && (
              <div style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
                <button
                  type="button"
                  onClick={handleSaveUserVisibility}
                  disabled={savingUserVisibility}
                  style={{ ...orgToolbarButtonStyle, backgroundColor: "#059669", color: "#fff" }}
                >
                  {savingUserVisibility ? "Saving…" : "Save user visibility"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelUserVisibility}
                  disabled={savingUserVisibility}
                  style={{ ...orgToolbarButtonStyle, backgroundColor: "#6b7280", color: "#fff" }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          {userFilterActive && (
            <div style={userFilterBannerStyle} role="status">
              <span>
                Dashboards visible to{" "}
                <strong>
                  {selectedFilterUser
                    ? formatUserPickerLabel(selectedFilterUser)
                    : `user ${filterUserId}`}
                </strong>
                {selectedFilterUser?.orgName ? ` · ${selectedFilterUser.orgName}` : ""}
              </span>
              {loadingUserScope ? (
                <span style={{ color: "#4b5563" }}>Resolving access…</span>
              ) : (
                userAccessSummary && (
                  <span style={{ color: "#4b5563" }}>
                    {userAccessSummary.total} of {userScopeDashboards.length} —{" "}
                    {userAccessSummary.user} direct, {userAccessSummary.org} via org,{" "}
                    {userAccessSummary.public} shown to all
                  </span>
                )
              )}
              <button
                type="button"
                onClick={() => {
                  setFilterUserId("");
                  setListPage(1);
                }}
                style={{
                  marginLeft: "auto",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #c7d2fe",
                  backgroundColor: "#ffffff",
                  color: "#3730a3",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Clear user filter
              </button>
            </div>
          )}
        </div>
      )}

      {error && <p style={{ color: "#b91c1c", marginBottom: "12px" }}>{error}</p>}

      {!isCreateTab && (
      <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "6px",
          marginBottom: "8px",
          flexWrap: "wrap",
        }}
      >
        {isListTab && (
          <span style={{ fontSize: "12px", color: "#6b7280", marginRight: "auto" }}>
            Wide table — scroll sideways, or zoom out to fit it in the panel.
          </span>
        )}
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Zoom</span>
        <button
          type="button"
          title="Zoom out"
          aria-label="Zoom table out"
          onClick={() => setTableZoom((z) => clampTableZoom(z - TABLE_ZOOM_STEP))}
          disabled={tableZoom <= TABLE_ZOOM_MIN}
          style={{ ...tableZoomButtonStyle, width: "28px", cursor: tableZoom <= TABLE_ZOOM_MIN ? "not-allowed" : "pointer", opacity: tableZoom <= TABLE_ZOOM_MIN ? 0.5 : 1 }}
        >
          −
        </button>
        <button
          type="button"
          title="Reset to 100%"
          onClick={() => setTableZoom(1)}
          style={{ ...tableZoomButtonStyle, minWidth: "52px" }}
        >
          {Math.round(tableZoom * 100)}%
        </button>
        <button
          type="button"
          title="Zoom in"
          aria-label="Zoom table in"
          onClick={() => setTableZoom((z) => clampTableZoom(z + TABLE_ZOOM_STEP))}
          disabled={tableZoom >= TABLE_ZOOM_MAX}
          style={{ ...tableZoomButtonStyle, width: "28px", cursor: tableZoom >= TABLE_ZOOM_MAX ? "not-allowed" : "pointer", opacity: tableZoom >= TABLE_ZOOM_MAX ? 0.5 : 1 }}
        >
          +
        </button>
        <button
          type="button"
          title="Zoom out just enough for the table to fit"
          onClick={fitTableToWidth}
          style={{ ...tableZoomButtonStyle, padding: "0 10px" }}
        >
          Fit
        </button>
      </div>
      <div ref={tableWrapperRef} style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {isStalenessTab ? (
                <>
                  <th style={{ ...thStyle, width: "6%" }}>ID</th>
                  <th style={{ ...thStyle, width: "22%" }}>Title</th>
                  <th style={{ ...thStyle, width: "12%" }}>Category</th>
                  <th style={{ ...thStyle, width: "12%" }}>Frequency</th>
                  <th style={{ ...thStyle, width: "17%" }}>Last Refreshed</th>
                  <th style={{ ...thStyle, width: "17%" }}>Next Due</th>
                  <th style={{ ...thStyle, width: "14%", whiteSpace: "nowrap" }}>Actions</th>
                </>
              ) : isOrgVisibilityView ? (
                <>
                  <th style={{ ...thStyle, width: "8%" }}>ID</th>
                  <th style={{ ...thStyle, width: "20%" }}>Title</th>
                  <th style={{ ...thStyle, width: "12%" }}>Category</th>
                  <th style={{ ...thStyle, width: "36%" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span>Org visibility</span>
                      <button
                        type="button"
                        onClick={handleOrgVisibilityHeaderEdit}
                        disabled={loading || totalDashboards === 0 || orgVisibilityEditable || savingOrgVisibility}
                        title="Edit organisation visibility for all dashboards"
                        aria-label="Edit organisation visibility"
                        style={{
                          background: "rgba(255,255,255,0.15)",
                          border: "1px solid rgba(255,255,255,0.5)",
                          borderRadius: "4px",
                          cursor:
                            loading || totalDashboards === 0 || orgVisibilityEditable || savingOrgVisibility
                              ? "not-allowed"
                              : "pointer",
                          padding: "4px",
                          display: "inline-flex",
                          alignItems: "center",
                          color: "#fff",
                          opacity: orgVisibilityEditable ? 0.45 : 1,
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </span>
                  </th>
                  <th style={{ ...thStyle, width: "24%" }}>Link</th>
                </>
              ) : isUserVisibilityView ? (
                <>
                  <th style={{ ...thStyle, width: "8%" }}>ID</th>
                  <th style={{ ...thStyle, width: "20%" }}>Title</th>
                  <th style={{ ...thStyle, width: "12%" }}>Category</th>
                  <th style={{ ...thStyle, width: "36%" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span>User visibility</span>
                      <button
                        type="button"
                        onClick={handleUserVisibilityHeaderEdit}
                        disabled={loading || totalDashboards === 0 || userVisibilityEditable || savingUserVisibility}
                        title="Edit user visibility for all dashboards"
                        aria-label="Edit user visibility"
                        style={{
                          background: "rgba(255,255,255,0.15)",
                          border: "1px solid rgba(255,255,255,0.5)",
                          borderRadius: "4px",
                          cursor:
                            loading || totalDashboards === 0 || userVisibilityEditable || savingUserVisibility
                              ? "not-allowed"
                              : "pointer",
                          padding: "4px",
                          display: "inline-flex",
                          alignItems: "center",
                          color: "#fff",
                          opacity: userVisibilityEditable ? 0.45 : 1,
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </span>
                  </th>
                  <th style={{ ...thStyle, width: "24%" }}>Link</th>
                </>
              ) : (
                <>
                  <th style={{ ...thStyle, width: "5%" }}>ID</th>
                  <th style={{ ...thStyle, width: "8%" }}>Title</th>
                  <th style={{ ...thStyle, width: "6%" }}>Category</th>
                  <th style={{ ...thStyle, width: "8%" }}>Description</th>
                  <th style={{ ...thStyle, width: "8%" }}>Org visibility</th>
                  <th style={{ ...thStyle, width: "8%" }}>User visibility</th>
                  <th style={{ ...thStyle, width: "8%" }}>Link</th>
                  <th style={{ ...thStyle, width: "4%", textAlign: "center" }} title="GitHub">
                    <FaGithub size={14} aria-label="GitHub" />
                  </th>
                  <th style={{ ...thStyle, width: "7%" }}>Sections</th>
                  <th style={{ ...thStyle, width: "7%" }}>Keywords</th>
                  <th style={{ ...thStyle, width: "6%" }}>Tickers</th>
                  <th style={{ ...thStyle, width: "6%" }}>Sectors</th>
                  <th style={{ ...thStyle, width: "6%" }}>Industries</th>
                  <th style={{ ...thStyle, width: "14%", whiteSpace: "nowrap" }}>Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {isStalenessTab ? (
              loadingStaleness ? (
                <>
                  {[...Array(6)].map((_, i) => (
                    <ShimmerRow key={i} colCount={tableColCount} />
                  ))}
                </>
              ) : visibleStaleness.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} style={{ ...tdStyle, textAlign: "center" }}>
                    {filterOverdueOnly ? "No overdue dashboards." : "No dashboards found."}
                  </td>
                </tr>
              ) : (
                visibleStaleness.map((d, idx) => {
                  const nextDue = nextRefreshAt(d);
                  const isOverdue = isDashboardOverdue(d);
                  const isRefreshing = !!refreshingDashboards[d.id];
                  return (
                    <tr key={d.id} style={idx % 2 === 1 ? trEvenStyle : undefined}>
                      <td style={{ ...tdStyle, color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>{d.id ?? "—"}</td>
                      <td style={tdStyle}>{d.title || "—"}</td>
                      <td style={tdStyle}>{d.category || "—"}</td>
                      <td style={tdStyle}>{formatRefreshFrequency(d.refresh_frequency_hours)}</td>
                      <td style={tdStyle}>
                        {formatStalenessTime(d.last_refreshed_at) || (
                          <span style={{ color: "#9ca3af" }}>Never</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {nextDue ? (
                          <span style={isOverdue ? { color: "#b91c1c" } : undefined}>
                            {nextDue.toLocaleString()}
                          </span>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          disabled={isRefreshing || !d.refresh_frequency_hours}
                          onClick={() => handleRefreshDashboard(d)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: "4px",
                            border: "1px solid #3730a3",
                            backgroundColor: isRefreshing ? "#e0e7ff" : "#eef2ff",
                            color: "#3730a3",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: isRefreshing || !d.refresh_frequency_hours ? "not-allowed" : "pointer",
                            opacity: !d.refresh_frequency_hours ? 0.45 : 1,
                            marginRight: "6px",
                          }}
                          title={
                            !d.refresh_frequency_hours
                              ? "Set a refresh frequency first"
                              : "Mark as refreshed now — stamps last refreshed and re-derives next due"
                          }
                        >
                          {isRefreshing ? "Refreshing…" : "↻ Refresh"}
                        </button>
                        <button
                          type="button"
                          onClick={() => startEdit(d)}
                          style={{
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4b5563",
                            backgroundColor: "#ffffff",
                            color: "#111827",
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )
            ) : loading || loadingOrgScope || loadingUserScope ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <ShimmerRow key={i} colCount={tableColCount} />
                ))}
              </>
            ) : visibleDashboards.length === 0 ? (
              <tr>
                <td colSpan={tableColCount} style={{ ...tdStyle, textAlign: "center" }}>
                  {userFilterActive
                    ? `No dashboards are visible to ${
                        selectedFilterUser
                          ? formatUserPickerLabel(selectedFilterUser)
                          : "this user"
                      }${hasOtherListFilters ? " with the current filters" : ""}.`
                    : filterCorporateView
                    ? "No corporate-assigned dashboards."
                    : hasListFilters
                    ? "No dashboards match your filters."
                    : "No dashboards found."}
                </td>
              </tr>
            ) : (
              visibleDashboards.map((d, idx) => {
                const originalOrgIds = getDashboardOrgIds(d);
                const currentOrgIds = orgVisibilityEditable
                  ? (orgVisibilityValues[d.id] ?? originalOrgIds)
                  : originalOrgIds;
                const originalUserIds = getDashboardUserIds(d);
                const currentUserIds = userVisibilityEditable
                  ? (userVisibilityValues[d.id] ?? originalUserIds)
                  : originalUserIds;
                const orgRowChanged = orgVisibilityEditable && !areSameIds(currentOrgIds, originalOrgIds);
                const userRowChanged = userVisibilityEditable && !areSameIds(currentUserIds, originalUserIds);
                const orgVisLabel = getDashboardOrgVisibilityLabel(d, availableOrgs);
                const userVisLabel = getDashboardUserVisibilityLabel(d);
                const accessReason =
                  userFilterActive && selectedFilterUser
                    ? resolveDashboardAccessForUser(d, selectedFilterUser).reason
                    : null;
                const sectorNames = (sectorIndustrySectors || []).map((s) => s?.name).filter(Boolean);
                const industryNames = [
                  ...new Set((sectorIndustryFlattened || []).map((t) => t?.industry).filter(Boolean)),
                ];
                const { sectors: displaySectors, industries: displayIndustries } =
                  splitDashboardSectorsFromApi(d, sectorNames, industryNames);
                const rowStyle =
                  orgRowChanged || userRowChanged
                    ? trOrgChangedStyle
                    : idx % 2 === 1
                    ? trEvenStyle
                    : undefined;
                const isCorporateRow = isCorporateAssociatedDashboard(d);
                const corporateRowAccent = isCorporateRow
                  ? {
                      boxShadow: "inset 3px 0 0 #0f766e",
                    }
                  : null;
                const combinedRowStyle = {
                  ...(rowStyle || {}),
                  ...(corporateRowAccent || {}),
                };
                const linkCell = d.type === "tradingview" ? (
                  <span style={{ fontSize: "12px", background: "#e0e7ff", color: "#3730a3", padding: "2px 6px", borderRadius: "4px", fontWeight: 500 }}>TradingView Widget Config</span>
                ) : d.link ? (
                  <a
                    href={d.link}
                    target="_blank"
                    rel="noreferrer"
                    title={d.link}
                    style={{
                      color: "#2563eb",
                      textDecoration: "underline",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.link}
                  </a>
                ) : (
                  "—"
                );

                const orgVisibilityCell = (
                  <td style={{ ...tdStyle, verticalAlign: "top", overflow: orgVisibilityEditable ? "visible" : "hidden" }}>
                    {orgVisibilityEditable ? (
                      <div style={{ width: "100%", maxWidth: "100%" }}>
                        <MultiSelectDropdown
                          options={buildOrgMultiSelectOptions(availableOrgs, currentOrgIds, (oid) => {
                            const fromApi = availableOrgs.find((o) => Number(o.id) === Number(oid));
                            return `${fromApi?.name ?? `Org ${oid}`} (id ${oid})`;
                          })}
                          value={currentOrgIds.map((id) => String(id))}
                          onChange={(vals) =>
                            setOrgVisibilityValues((prev) => ({
                              ...prev,
                              [d.id]: parseOrgIdsFromMultiSelectValues(vals),
                            }))
                          }
                          placeholder="Nothing Selected means Shown to All"
                          searchable
                          optionColumns={2}
                          popout
                          aria-label={`Organisation visibility for ${d.title || d.id}`}
                        />
                      </div>
                    ) : (
                      <span
                        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={orgVisLabel}
                      >
                        {orgVisLabel}
                      </span>
                    )}
                  </td>
                );

                const userVisibilityCell = (
                  <td style={{ ...tdStyle, verticalAlign: "top", overflow: userVisibilityEditable ? "visible" : "hidden" }}>
                    {userVisibilityEditable ? (
                      <div style={{ width: "100%", maxWidth: "100%" }}>
                        <MultiSelectDropdown
                          options={buildUserMultiSelectOptions(
                            orgUsersForPicker,
                            currentUserIds,
                            (uid) => {
                              const embedded = Array.isArray(d.users) ? d.users : [];
                              const match = embedded.find((u) => Number(u.user_id) === Number(uid));
                              if (match?.name) return `${match.name} (id ${uid})`;
                              if (match?.email) return `${match.email} (id ${uid})`;
                              return `User ${uid} (id ${uid})`;
                            }
                          )}
                          value={currentUserIds.map((id) => String(id))}
                          onChange={(vals) =>
                            setUserVisibilityValues((prev) => ({
                              ...prev,
                              [d.id]: parseUserIdsFromMultiSelectValues(vals),
                            }))
                          }
                          placeholder="Nothing selected = no user restriction"
                          searchable
                          optionColumns={2}
                          popout
                          aria-label={`User visibility for ${d.title || d.id}`}
                        />
                      </div>
                    ) : (
                      <span
                        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={userVisLabel}
                      >
                        {userVisLabel}
                      </span>
                    )}
                  </td>
                );

                return (
                <tr key={d.id} style={Object.keys(combinedRowStyle).length ? combinedRowStyle : undefined}>
                  <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums", color: "#6b7280" }}>
                    {d.id ?? "—"}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span>{d.title || "—"}</span>
                      {accessReason && (
                        <span
                          style={accessReasonPillStyle(accessReason)}
                          title={ACCESS_REASON_HINTS[accessReason]}
                        >
                          {formatDashboardAccessReason(accessReason, selectedFilterUser)}
                        </span>
                      )}
                      {isCorporateRow && (
                        <span
                          style={corporateAudiencePillStyle}
                          title={
                            d.corporateAssigneeCount > 0
                              ? `Corporate assignees: ${d.corporateAssigneeCount}. Includes org assignment when the org has corporate users.`
                              : "Has corporate assignees (user and/or org that contains corporate users)."
                          }
                        >
                          <FiLock size={11} aria-hidden />
                          Corporate
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>{d.category || "—"}</td>
                  {isOrgVisibilityView ? (
                    <>
                      {orgVisibilityCell}
                      <td style={{ ...tdStyle, overflow: "hidden", wordBreak: "break-word" }}>{linkCell}</td>
                    </>
                  ) : isUserVisibilityView ? (
                    <>
                      {userVisibilityCell}
                      <td style={{ ...tdStyle, overflow: "hidden", wordBreak: "break-word" }}>{linkCell}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell text={d.description || ""} emptyLabel="—" />
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell text={orgVisLabel} emptyLabel="—" emptyMuted={false} />
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell text={userVisLabel} emptyLabel="—" emptyMuted={false} />
                      </td>
                      <td style={{ ...tdStyle, overflow: "hidden", wordBreak: "break-word", verticalAlign: "top" }}>
                        {d.type === "tradingview" ? (
                          linkCell
                        ) : d.link ? (
                          <div style={{ minWidth: 0 }}>
                            <a
                              href={d.link}
                              target="_blank"
                              rel="noreferrer"
                              title={d.link}
                              style={{
                                color: "#2563eb",
                                textDecoration: "underline",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              }}
                            >
                              {d.link}
                            </a>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center", verticalAlign: "top" }}>
                        {(() => {
                          const githubUrl = (d.githubLink || d.github_link || "").trim();
                          if (githubUrl) {
                            return (
                              <a
                                href={githubUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={githubUrl}
                                aria-label={`Open GitHub: ${githubUrl}`}
                                style={{
                                  color: "#111827",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "4px",
                                  borderRadius: "4px",
                                }}
                              >
                                <FaGithub size={18} />
                              </a>
                            );
                          }
                          return (
                            <span
                              title="No GitHub link"
                              aria-label="No GitHub link"
                              style={{
                                color: "#d1d5db",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px",
                              }}
                            >
                              <FaGithub size={18} />
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden", lineHeight: "normal" }}>
                        <DashboardSectionsTableCell sections={d.sections} />
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell
                          text={
                            Array.isArray(d.keywords) && d.keywords.length > 0
                              ? d.keywords.join(", ")
                              : ""
                          }
                          emptyLabel="—"
                        />
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell
                          text={
                            Array.isArray(d.tickers) && d.tickers.length > 0
                              ? d.tickers.join(", ")
                              : ""
                          }
                          emptyLabel="All tickers"
                        />
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell
                          text={displaySectors.length > 0 ? displaySectors.join(", ") : ""}
                          emptyLabel="All sectors"
                        />
                      </td>
                      <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                        <ExpandableCell
                          text={displayIndustries.length > 0 ? displayIndustries.join(", ") : ""}
                          emptyLabel="All industries"
                        />
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap", textAlign: "right", width: "1%", verticalAlign: "top" }}>
                        <button
                          type="button"
                          onClick={() => startEdit(d)}
                          style={{
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4b5563",
                            backgroundColor: "#ffffff",
                            color: "#111827",
                            fontSize: "12px",
                            cursor: "pointer",
                            marginRight: "6px",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenThumbnailModal(d)}
                          title="Upload a fallback thumbnail for this dashboard"
                          style={{
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #3730a3",
                            backgroundColor: "#eef2ff",
                            color: "#3730a3",
                            fontSize: "12px",
                            cursor: "pointer",
                            marginRight: "6px",
                          }}
                        >
                          Thumbnail
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(d)}
                          style={{
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #dc2626",
                            backgroundColor: "#fef2f2",
                            color: "#b91c1c",
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && !loadingOrgScope && !loadingUserScope && (isVisibilityView && (orgVisibilityEditable || userVisibilityEditable || orgVisibilityScope !== "all") ? visibleDashboards.length > 0 : listTotal > 0) && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", marginTop: "12px" }}>
          <p style={{ margin: 0, fontSize: "13px", color: "#4b5563" }}>
            {isOrgVisibilityView && orgVisibilityEditable
              ? `Editing org visibility for ${visibleDashboards.length} dashboard${visibleDashboards.length === 1 ? "" : "s"}.`
              : isUserVisibilityView && userVisibilityEditable
              ? `Editing user visibility for ${visibleDashboards.length} dashboard${visibleDashboards.length === 1 ? "" : "s"}.`
              : isOrgVisibilityView && orgVisibilityScope !== "all"
              ? `Showing ${visibleDashboards.length} dashboard${visibleDashboards.length === 1 ? "" : "s"} (${
                  orgVisibilityScope === "all_users" ? "shown to all users" : "shown by customers"
                }).`
              : userFilterActive
              ? `Showing ${listStart}–${listEnd} of ${listTotal} dashboard${
                  listTotal === 1 ? "" : "s"
                } visible to ${
                  selectedFilterUser ? formatUserPickerLabel(selectedFilterUser) : "this user"
                }.`
              : hasListFilters
              ? `Showing ${listStart}–${listEnd} of ${listTotal} dashboards (filtered).`
              : `Showing ${listStart}–${listEnd} of ${listTotal} dashboard${listTotal === 1 ? "" : "s"}.`}
          </p>
          {!(isVisibilityView && (orgVisibilityEditable || userVisibilityEditable || orgVisibilityScope !== "all")) && totalPages > 1 && (
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              <button
                type="button"
                onClick={() => setListPage((p) => Math.max(1, p - 1))}
                disabled={listPage <= 1}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  cursor: listPage <= 1 ? "not-allowed" : "pointer",
                  fontSize: "13px",
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: "13px", color: "#4b5563", alignSelf: "center" }}>
                Page {listPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                disabled={listPage >= totalPages}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  cursor: listPage >= totalPages ? "not-allowed" : "pointer",
                  fontSize: "13px",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {confirmAction?.type === "delete" && (
        <div
          style={modalOverlayStyle}
          onClick={handleDeleteCancel}
          onKeyDown={(e) => {
            if (e.key === "Escape") handleDeleteCancel();
          }}
        >
          <form
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              if (confirmAction.dashboard) performDelete(confirmAction.dashboard);
            }}
          >
            <div style={modalTitleStyle}>Delete dashboard</div>
            <p style={modalBodyStyle}>
              This deletes{" "}
              <strong>
                {confirmAction.dashboard?.title || `dashboard ${confirmAction.dashboard?.id}`}
              </strong>{" "}
              from the database. This action cannot be undone.
            </p>
            <label
              htmlFor="dashboard-delete-confirm"
              style={{ ...modalBodyStyle, display: "block", marginBottom: "8px" }}
            >
              Type <strong>delete</strong> to confirm:
            </label>
            <input
              id="dashboard-delete-confirm"
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="delete"
              style={modalDangerInputStyle}
              autoFocus
              autoComplete="off"
              disabled={deleting}
            />
            <div style={modalButtonsStyle}>
              <button
                type="button"
                style={modalCancelButtonStyle}
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={isDeleteConfirmed ? modalDeleteButtonStyle : modalDeleteButtonDisabledStyle}
                disabled={deleting || !isDeleteConfirmed}
                title={isDeleteConfirmed ? "Delete this dashboard" : 'Type "delete" to enable'}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmAction && confirmAction.type !== "delete" && (
        <div
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            backgroundColor: "#111827",
            color: "#f9fafb",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            zIndex: 1200,
            maxWidth: "320px",
          }}
        >
          <span style={{ fontSize: "14px" }}>
            {formMode === "edit"
              ? "Save changes to this dashboard?"
              : "Create this dashboard?"}
          </span>
          <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
            <button
              type="button"
              onClick={() => {
                if (confirmAction.type === "saveEdit") {
                  performSave("edit");
                } else if (confirmAction.type === "saveCreate") {
                  performSave("create");
                }
              }}
              style={{
                padding: "6px 10px",
                borderRadius: "4px",
                border: "none",
                backgroundColor: "#16a34a",
                color: "#f9fafb",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setConfirmAction(null)}
              style={{
                padding: "6px 10px",
                borderRadius: "4px",
                border: "1px solid #6b7280",
                backgroundColor: "transparent",
                color: "#e5e7eb",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              No
            </button>
          </div>
        </div>
      )}
      {thumbnailDashboard && (
        <DashboardThumbnailModal
          dashboard={thumbnailDashboard}
          onClose={handleCloseThumbnailModal}
        />
      )}
    </div>
  );
};

export default Dashboards;

